import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { listBulkJobs } from "../../../../services/bulkJobService";
import { fetchAutomationRoutingOptions } from "../../../../services/organizationService";
import { HiUsers, HiUserAdd, HiOutlineClock, HiCheck, HiXCircle } from "react-icons/hi";
import YhTable, { YhTableColumn, YhTableCellParams } from "../../../../ui/YhTable";
import YhInput from "../../../../ui/YhInput";
import YhModal from "../../../../ui/YhModal";
import StageDetailsPanel from "../../../../ui/StageDetailsPanel";
import YhMultiSelect from "../../../../ui/YhMultiSelect";
import YhSelect from "../../../../ui/YhSelect";
import YhButton from "../../../../ui/YhButton";
import YhSourceIcon from "../../../../ui/YhSourceIcon";
import LeadStatCard from "../../../../ui/LeadStatCard";
import { YhDateRangePicker } from "../../../../ui/YhDateTimePicker";
import LeadFiltersDrawer, {
  countActiveLeadFilters,
  LeadFilterValues,
} from "../../../../ui/LeadFiltersDrawer";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import {
  canAssignToSelf,
  canEditLeadSource,
  defaultReopenStage,
  isNiOrLostStage,
} from "../../../../helpers/salesLeadHelpers";
import {
  filterByViewerTeams,
  loadViewerTeamIds,
} from "../../../../helpers/assignmentScope";
import StageReasonModal from "../../../../global/components/StageReasonModal";
import { useLeadRealtimeRefresh } from "../../../../utils/useLeadRealtimeRefresh";
import {
  FILTER_KEYS,
  loadLeadListFilters,
  saveLeadListFilters,
  clearLeadListFilters,
  setLeadListReturnPath,
  expandStageSelection,
  parseListPagination,
  withListPagination,
  PersistedAllLeadsFilters,
} from "../../../../helpers/leadListFilters";
import {
  compareLeadsNewestFirst,
  formatLeadDateParts,
  isFreshLeadToday,
  leadArrivalTime,
  patchLeadStageOnRow,
  relativeLeadAgeLabel,
} from "../../../../helpers/leadDate";
import CustomButton from "../../../../global/components/CustomButton/CustomButton";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/close.svg";
import urls from "../../../../global/constants/UrlConstants";
import {
  OrgNameCell,
  useOrgNameMap,
} from "../../../../Shared/Organization/OrganizationSelect";
import {
  formatPhoneKeepingCountryCode,
  resolveLeadPhoneForDisplay,
} from "../../../../Shared/Phone/PhoneWithCountryCode";
import {
  selectActiveOrganizationId,
  selectPrivileges,
} from "../../../../redux/authSlice";
import {
  canExportLeadsFor,
  canViewPayments,
} from "../../../../Shared/AppDrawer/menuPrivileges";
import FeedbackBulkSendModal from "./FeedbackBulkSendModal";
import BulkAssignModal from "./BulkAssignModal";
import BulkStageModal from "./BulkStageModal";
import BulkStatusUploadModal from "./BulkStatusUploadModal";
import {
  assignToMembers,
  changeAt,
  changeStage,
  deleteReminder,
  getAllDepartmentsForFilter,
  getAllForms,
  getAllStage,
  getAllTeamData,
  getAllTeamMembersData,
  getAllUsers,
  getAllMatchingLeadIds,
  getMembersForFilter,
  getTeamsForFilter,
  // getDownloadLeads,
  getDownloadLeadsExcle,
  getReminder,
  getReminderData,
  saveReminder,
} from "./ShowAllService";
import notifiers from "../../../../global/constants/NotificationConstants";
import CustomLoader from "../../../../global/components/CustomLoader/CustomLoader";
import CustomDialog from "../../../../global/components/CustomModal/CustomModal";
import CustomRadioButton from "../../../../global/components/CustomRadioButton/CustomRadioButton";
import AddLoan from "../AddLoan/AddLoan";
import LoanList from "../AddLoan/RoleTabContent";
import { store } from "../../../../utils/store";
import { ReactComponent as VerticalDotIcon } from "../../../../assets/icons/verticalDot.svg";
import LeadDetailsComponent from "../../../../global/components/sidePanel/leadDetail";
import {
  getAllSource,
  getRemark,
  saveRemark,
} from "../../../MyLead/components/ShowAll/ShowAllService";
import { applyLicenseKey } from "../../../../utils/License";
import { getStage } from "../../../MetaData/components/MetaList/components/LeadStageData/LeadStageListService";
import { updatePersonalDetail } from "../stepper/StepperService";
import { getAllHierarchyMeta } from "../../../MetaData/components/MetaList/components/CompanyHierarchyList/companyHierarchyService";

/** react-icons + React 19 type bridge */
type IconComp = React.ComponentType<{ size?: number; className?: string }>;
const UsersIcon = HiUsers as IconComp;
const UserAddIcon = HiUserAdd as IconComp;
const ClockIcon = HiOutlineClock as IconComp;
const CheckIconRi = HiCheck as IconComp;
const XCircleIcon = HiXCircle as IconComp;
const DownloadSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <path d="M7 10l5 5 5-5" />
    <path d="M12 15V3" />
  </svg>
);

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const SearchSpinner = () => (
  <svg
    className="animate-spin text-[#A02448]"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.25" />
    <path
      d="M21 12a9 9 0 00-9-9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const EditSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const leadActivityTime = (row: any) => leadArrivalTime(row);
const relativeLeadTag = (dateTime: any): string | null =>
  relativeLeadAgeLabel(dateTime);
const sortLeadsByAssigned = compareLeadsNewestFirst;

const ShowAll = () => {
  interface Lead {
    [key: string]: string | number | null | undefined;
  }
  const history = useHistory();
  const location = useLocation();
  const persistedFilters =
    loadLeadListFilters<PersistedAllLeadsFilters>(
      FILTER_KEYS.allLeads,
      String(store.getState().auth?.userId || "")
    ) || {};
  const [selectedUploadLeadIds, setSelectedUploadLeadIds] = useState<string[]>(
    () => persistedFilters.selectedUploadLeadIds || []
  );
  const [bulkSetOptions, setBulkSetOptions] = useState<
    { label: string; value: string }[]
  >([]);
  // useSelector (not store.getState) so the gates re-evaluate when an admin
  // changes this user's privileges without a full remount.
  const privileges = useSelector(selectPrivileges) || {};
  const canExportLeads = canExportLeadsFor(privileges, "All Lead");
  const showPaymentColumn = canViewPayments(privileges);
  const orgNameMap = useOrgNameMap();
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  /** When true, selection spans all pages matching current filters */
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [feedbackSendOpen, setFeedbackSendOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkStageOpen, setBulkStageOpen] = useState(false);
  const [bulkStatusUploadOpen, setBulkStatusUploadOpen] = useState(false);
  const [userDataSource, setUserDataSource] = useState<any>([]);
  const [userDataSource1, setUserDataSource1] = useState<any>([]);
  const [addNewTeamDialog, setNewTeamDialog] = useState<any>({
    open: false,
    edit: false,
  });

  const [eventForEdit, setEventForEdit] = React.useState();
  const [userId, setUserId] = useState("");
  const [apiResponse, setApiResponse] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setdownloadLoading] = useState(false);
  const [response, setResponse] = useState<any>();
  const [selectedUser, setSelectedUser] = useState({ id: "", status: 0 });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [userRow_Id, setUserRow_Id] = useState<string>();
  const [rowStatus, setRowStatus] = useState();
  const [showSideBar, setShowSideBar] = useState(false);
  const [dynamicGridValue, setDynamicGridValue] = useState<any>();
  const [addinationalValue, setaddinationalValue] = useState<any>();
  const [leadOption, setLeadOption] = useState<any>([]);
  const [leadOptionMembers, setLeadOptionMembersas] = useState<any>([]);
  const [reminderStatus, setReminderStatus] = useState<any>("");
  const currentDateTime = new Date().toISOString().slice(0, 16);
  const [reminderData, setReminderData] = useState<any>({
    date: currentDateTime,
  });
  const [responseValuereminder, setResponseValuereminder] = useState<any>([]);
  const [formName, setFormName] = useState<any>([]);
  const [tableData, setTableData] = useState<any>([]);
  const [stageData, setStageData] = useState<any>([]);
  const [selectedOptions, setSelectedOptions] = useState<any>([]);
  const [selectedOptionsMembers, setSelectedOptionsMembers] = useState<any>([]);
  const [responseValueremark, setResponseValueremark] = useState<any>([]);
  const [idForTeam, setIdForTeam] = useState<any>([]);
  const [idForMember, setIdForMember] = useState<any>([]);
  const [leadData, setLeadData] = useState<any>();
  const [openModal, setOpenModal] = useState<any>({
    name: "",
    data: {},
    open: false,
  });
  const [paginationModel, setPaginationModel] = React.useState(() =>
    parseListPagination(location.search, {
      page: persistedFilters.page || 0,
      pageSize: persistedFilters.pageSize || 25,
    })
  );
  const [metaData, setMetaData] = useState<any>({});
  const [stageSourse, setStageSourse] = useState<any>([]);
  const [remData, setRemData] = useState<any>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [isSearchPending, setIsSearchPending] = useState(false);
  const [selectedStages, setSelectedStages] = useState<string[]>(
    () => persistedFilters.selectedStages || []
  );
  const [selectedSources, setSelectedSources] = useState<string[]>(
    () => persistedFilters.selectedSources || []
  );
  /** all | assigned | unassigned */
  const [assignmentFilter, setAssignmentFilter] = useState<string>(
    () => persistedFilters.assignmentFilter || "all"
  );
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>(() => {
    const teams = persistedFilters.selectedTeamIds || [];
    if (!teams.length) return [];
    return persistedFilters.selectedMemberIds || [];
  });
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>(
    () => persistedFilters.selectedDepartmentIds || []
  );
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(
    () => persistedFilters.selectedTeamIds || []
  );
  const [departmentFilterOptions, setDepartmentFilterOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [teamFilterOptions, setTeamFilterOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [teamMemberFilterOptions, setTeamMemberFilterOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [memberFilterOptions, setMemberFilterOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [formLead, setFormLead] = useState<any>(
    () => persistedFilters.formLead || []
  );
  const [selectedDateRange, setSelectedDateRange] = useState<any>(
    () => persistedFilters.selectedDateRange || {}
  );
  const [freshFilter, setFreshFilter] = useState(
    () => Boolean(persistedFilters.freshFilter)
  );
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const editPopoverRef = useRef<HTMLDivElement>(null);
  const [type, setType] = useState("");
  const [allSatgedata, setAllStagedata] = useState<any>([]);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [selectedRow, setSelectedRow] = useState<null | any>(null);
  const [hierarchyMap, setHierarchyMap] = useState<{ [key: string]: string }>(
    {}
  );
  const [reminders, setReminders] = useState<{ [key: string]: any }>({});
  const [customerConfirm, setCustomerConfirm] = useState<{
    open: boolean;
    stage: string;
    lead: any;
  }>({ open: false, stage: "", lead: null });
  const [convertingCustomer, setConvertingCustomer] = useState(false);
  const prevOrgIdRef = useRef<string | null>(null);
  useEffect(() => {
    applyLicenseKey();
  }, []);

  useEffect(() => {
    // Clear form filter only when org actually changes (not on first mount —
    // otherwise restored session filters are wiped).
    if (
      prevOrgIdRef.current !== null &&
      prevOrgIdRef.current !== activeOrganizationId
    ) {
      setFormLead([]);
      setTableData([]);
    }
    prevOrgIdRef.current = activeOrganizationId;
  }, [activeOrganizationId]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (editPopoverRef.current && !editPopoverRef.current.contains(target)) {
        setAnchorEl(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (searchInput === search) {
      setIsSearchPending(false);
      return;
    }
    setIsSearchPending(true);
    const timer = window.setTimeout(() => {
      setPaginationModel((prev) =>
        prev.page === 0 ? prev : { ...prev, page: 0 }
      );
      setSearch(searchInput);
      setIsSearchPending(false);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput, search]);

  // Stages, sources, forms, and hierarchy are for Filters and the row menu.
  // The lead table itself only needs getLeadList.
  const catalogKey = useRef("");
  const loadLeadCatalogs = () => {
    const key = String(activeOrganizationId || "none");
    if (catalogKey.current === key) return;
    catalogKey.current = key;
    getAllStage()
      .then((response2) => {
        const val = (response2?.data || []).map((e: any) => e.status_name);
        setStageData(val?.filter((name: any) => name !== "Initial"));
      })
      .catch(() => undefined);
    getAllSource()
      .then((response32) => {
        const seen = new Set<string>();
        const names: string[] = [];
        for (const e of response32?.data || []) {
          const name = String(e?.source_name || "").trim();
          if (!name) continue;
          const sourceKey = name.toLowerCase();
          if (seen.has(sourceKey)) continue;
          seen.add(sourceKey);
          names.push(name);
        }
        setStageSourse(names);
      })
      .catch(() => undefined);
    getStage()
      .then((resData) => setAllStagedata(resData?.data || []))
      .catch(() => undefined);
    getAllHierarchyMeta()
      .then((response) => {
        const hierarchyData = (response?.data || []).reduce((acc: any, e: any) => {
          acc[e._id] = e.hierarchyName;
          return acc;
        }, {});
        setHierarchyMap(hierarchyData);
      })
      .catch(() => undefined);
    getAllForms()
      .then((response3) => {
        setFormName(
          (response3?.data || [])
            ?.filter((item: any) => item.status === "1")
            ?.map((i: any) => ({ id: i._id, label: i.form_name }))
        );
      })
      .catch(() => setFormName([]));
  };

  useEffect(() => {
    if (!filtersOpen) return;
    loadLeadCatalogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersOpen, activeOrganizationId]);

  useEffect(() => {
    if (!filtersOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const orgId =
          activeOrganizationId &&
          activeOrganizationId !== "all" &&
          activeOrganizationId !== "*"
            ? String(activeOrganizationId)
            : "";
        const res = await listBulkJobs({
          type: "leads",
          status: "completed,partial,failed",
          limit: 100,
          ...(orgId ? { organizationId: orgId } : {}),
        });
        const items = Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.items)
            ? res.items
            : Array.isArray(res?.data)
              ? res.data
              : [];
        if (cancelled) return;
        setBulkSetOptions(
          items
            .filter((j: any) => Number(j?.totals?.success || 0) > 0)
            .map((j: any) => ({
              value: String(j._id),
              label:
                String(j.setName || "").trim() ||
                String(j.subSource || "").trim() ||
                String(j.originalFileName || "").trim() ||
                `Set ${String(j._id).slice(-6)}`,
            }))
        );
      } catch {
        if (!cancelled) setBulkSetOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filtersOpen, activeOrganizationId]);

  // B11: load org members for All Leads member filter
  useEffect(() => {
    if (!filtersOpen) return;
    let cancelled = false;
    const orgId = String(activeOrganizationId || "").trim();
    if (!orgId || orgId === "all" || orgId === "*") {
      setMemberFilterOptions([]);
      return;
    }
    (async () => {
      try {
        const res = await fetchAutomationRoutingOptions(orgId);
        const data = res?.data || res || {};
        const members = Array.isArray(data.members) ? data.members : [];
        if (cancelled) return;
        const opts = members
          .map((m: any) => ({
            label: String(m.name || m.user_name || m.email || m.userId || ""),
            value: String(m.userId || m._id || ""),
          }))
          .filter((o: { label: string; value: string }) => o.value);
        // de-dupe by value
        const seen = new Set<string>();
        setMemberFilterOptions(
          opts.filter((o: { value: string }) => {
            if (seen.has(o.value)) return false;
            seen.add(o.value);
            return true;
          })
        );
      } catch {
        if (!cancelled) setMemberFilterOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filtersOpen, activeOrganizationId]);

  // Department options for All Leads filters
  useEffect(() => {
    if (!filtersOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getAllDepartmentsForFilter();
        if (cancelled) return;
        const opts = (res?.data || [])
          .map((d: any) => ({
            label: String(d.department_name || d.name || "").trim(),
            value: String(d._id || "").trim(),
          }))
          .filter((o: { label: string; value: string }) => o.value && o.label);
        setDepartmentFilterOptions(opts);
      } catch {
        if (!cancelled) setDepartmentFilterOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filtersOpen, activeOrganizationId]);

  // Teams cascade from selected departments (also when drawer drafts depts)
  const loadTeamsForDepartments = async (departmentIds: string[]) => {
    if (!departmentIds.length) {
      setTeamFilterOptions([]);
      return;
    }
    try {
      const res = await getTeamsForFilter(departmentIds);
      const flat: { label: string; value: string }[] = [];
      const seen = new Set<string>();
      const pushTeam = (id: string, name: string) => {
        if (!id || seen.has(id)) return;
        seen.add(id);
        flat.push({ value: id, label: name });
      };
      for (const t of res?.data || []) {
        pushTeam(String(t._id || ""), String(t.team_name || "").trim());
        for (const st of t.sub_teams || []) {
          pushTeam(
            String(st._id || ""),
            `↳ ${String(st.team_name || "").trim()}`
          );
        }
      }
      setTeamFilterOptions(flat.filter((o) => o.label));
    } catch {
      setTeamFilterOptions([]);
    }
  };

  const loadMembersForTeams = async (teamIds: string[]) => {
    if (!teamIds.length) {
      setTeamMemberFilterOptions([]);
      return;
    }
    try {
      const res = await getMembersForFilter(teamIds);
      const opts: { label: string; value: string }[] = [];
      const seen = new Set<string>();
      for (const t of res?.data || []) {
        for (const m of t.team_members || []) {
          const user = Array.isArray(m.users) ? m.users[0] : m.users || m;
          const id = String(
            m.userId || user?._id || user?.userId || m._id || ""
          ).trim();
          const name = String(user?.name || m.name || "").trim();
          if (!id || seen.has(id)) continue;
          seen.add(id);
          opts.push({ value: id, label: name || id });
        }
      }
      setTeamMemberFilterOptions(opts);
    } catch {
      setTeamMemberFilterOptions([]);
    }
  };

  useEffect(() => {
    void loadTeamsForDepartments(selectedDepartmentIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartmentIds]);

  useEffect(() => {
    void loadMembersForTeams(selectedTeamIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamIds]);

  // Deep-link: /all-leads?uploadLeadId=...
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const raw =
      params.get("uploadLeadId") ||
      params.get("bulkJobId") ||
      params.get("bulkSetId") ||
      "";
    if (!raw) return;
    const ids = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (ids.length) setSelectedUploadLeadIds(ids);
  }, [location.search]);

  // Persist filters so navigating to lead details / back keeps them
  useEffect(() => {
    saveLeadListFilters(
      FILTER_KEYS.allLeads,
      {
      search,
      searchInput,
      selectedStages,
      selectedSources,
      assignmentFilter,
      formLead,
      selectedUploadLeadIds,
      selectedMemberIds,
      selectedDepartmentIds,
      selectedTeamIds,
      selectedDateRange,
      freshFilter,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    } as PersistedAllLeadsFilters,
      String(store.getState().auth?.userId || "")
    );
  }, [
    search,
    searchInput,
    selectedStages,
    selectedSources,
    assignmentFilter,
    formLead,
    selectedUploadLeadIds,
    selectedMemberIds,
    selectedDepartmentIds,
    selectedTeamIds,
    selectedDateRange,
    freshFilter,
    paginationModel.page,
    paginationModel.pageSize,
  ]);

  // Keep page/pageSize in the URL so refresh / back from lead details stays put
  useEffect(() => {
    const next = withListPagination(
      location.pathname || urls.ALLLEADFORMLIST,
      paginationModel.page,
      paginationModel.pageSize,
      location.search
    );
    const current = `${location.pathname || ""}${location.search || ""}`;
    if (next !== current) {
      history.replace(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  // Restore form chip objects once form options are loaded
  useEffect(() => {
    if (!formLead?.length || !formName?.length) return;
    const selected = formName.filter((item: any) =>
      formLead.some((id: any) => String(id) === String(item.id))
    );
    if (selected.length) setTableData(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formName]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      tableDataShowHandler();
    }, 40);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formLead,
    search,
    paginationModel,
    selectedDateRange,
    selectedStages,
    selectedSources,
    assignmentFilter,
    selectedUploadLeadIds,
    selectedMemberIds,
    selectedDepartmentIds,
    selectedTeamIds,
    freshFilter,
    activeOrganizationId,
  ]);

  // Keep Assign (N) accurate — drop selection when filters / org change
  useEffect(() => {
    setSelectedLeadIds([]);
    setSelectAllMatching(false);
  }, [
    formLead,
    search,
    selectedDateRange,
    selectedStages,
    selectedSources,
    assignmentFilter,
    selectedUploadLeadIds,
    selectedMemberIds,
    selectedDepartmentIds,
    selectedTeamIds,
    freshFilter,
    activeOrganizationId,
  ]);


  const flattenStageData = (
    data: any[]
  ): { label: string; isParent: boolean }[] => {
    const result: { label: string; isParent: boolean }[] = [];

    const extractStageNames = (stages: any[]) => {

      stages.forEach((stage) => {
        if (stage.stage_Name) {
          result.push({ label: stage.stage_Name, isParent: true }); // Parent entry
        }
        if (stage.sub_Stage_name && Array.isArray(stage.sub_Stage_name)) {
          stage.sub_Stage_name.forEach((subStage: any) => {
            result.push({ label: subStage.stage_Name, isParent: false }); // Child entry
          });
        }
      });
    };

    extractStageNames(data);
    return result;
  };

  const stageOptions = flattenStageData(allSatgedata);
  const remindersStarted = useRef(false);
  const loadReminders = () => {
    if (remindersStarted.current) return;
    remindersStarted.current = true;
    getReminderData()
      .then((response5) => {
        setRemData((response5?.data || []).map((e: any) => e));
      })
      .catch((error) => {
        remindersStarted.current = false;
        console.error("Error fetching reminder data:", error);
      });
  };

  const handlePageSizeChange = (event: any) => {
    setPaginationModel({
      ...paginationModel,
      pageSize: event.target.value,
      page: 0,
    });
  };
  const filterLeadByForm = async (value: any[]) => {
    setFormLead(value);
  };


  const onClickHandler = () => {
    setUserId("");
    history.push(urls.ADDLEADPAGE);
  };

  const closeHandler = () => {
    setNewTeamDialog(false);
  };
  const cleanId = (val: any) =>
    String(val ?? "")
      .replace(/['"]/g, "")
      .trim();

  /** Team objects: prefer team_id / _id (never user fields). */
  const parseAssignedTeamIds = (raw: any): string[] => {
    if (!raw || raw === "NA") return [];
    if (Array.isArray(raw)) {
      return raw
        .map((item: any) => {
          if (item == null) return "";
          if (typeof item === "string" || typeof item === "number") {
            return cleanId(item);
          }
          return cleanId(item.team_id || item._id || item.id);
        })
        .filter(Boolean);
    }
    if (typeof raw === "string") {
      return raw
        .split(",")
        .map((s) => cleanId(s))
        .filter((s) => s && s !== "NA");
    }
    return [];
  };

  /**
   * Member / user objects: prefer user id fields.
   * Never use team_id — user docs often have team_id (JSON array of teams),
   * which would break Member multi-select preselection (Sahil not selected).
   */
  const parseAssignedMemberIds = (raw: any): string[] => {
    if (!raw || raw === "NA") return [];
    const out: string[] = [];
    const push = (item: any) => {
      if (item == null || item === "") return;
      if (Array.isArray(item)) {
        item.forEach(push);
        return;
      }
      if (typeof item === "string" || typeof item === "number") {
        String(item)
          .split(",")
          .forEach((s) => {
            const id = cleanId(s);
            if (id && id !== "NA") out.push(id);
          });
        return;
      }
      const id = cleanId(item.user_id || item.userId || item._id || item.id);
      if (id) out.push(id);
    };
    push(raw);
    return Array.from(new Set(out));
  };

  const getAssignedTeamIds = (assignTo: any) =>
    new Set(parseAssignedTeamIds(assignTo));

  const getAssignedMemberIds = (assignToUser: any) =>
    new Set(parseAssignedMemberIds(assignToUser));

  /** Keep assignees that belong to the lead's teams (matches Team List modal). */
  const filterMembersForAssignedTeams = (
    assignToUser: any,
    assignedTeamIds: Set<string>
  ) => {
    if (!Array.isArray(assignToUser)) return assignToUser;
    if (!assignedTeamIds.size) return assignToUser;
    const matched = assignToUser.filter((m: any) => {
      try {
        const raw = m?.team_id;
        if (raw == null || raw === "" || raw === "NA") return true;
        const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (!Array.isArray(arr) || arr.length === 0) return true;
        return arr.some((tid: any) => assignedTeamIds.has(String(tid)));
      } catch {
        return true;
      }
    });
    return matched.length ? matched : assignToUser;
  };

  const isFlagOn = (val: any) =>
    val === 1 || val === "1" || val === true;

  const handelDropdown = async (id: any) => {
    setLoading(true);
    try {
      setApiResponse(true);
      const row = id?.row || dynamicGridValue || {};
      const assignedTeamIds = getAssignedTeamIds(row.AssignTo);
      const leadMembersForSelect = filterMembersForAssignedTeams(
        row.AssignToUser,
        assignedTeamIds
      );
      const assignedMemberIds = getAssignedMemberIds(leadMembersForSelect);

      const response1 = await getAllTeamData(id?.id || row?._id);
      const viewerTeamIds = await loadViewerTeamIds();
      const assignToData = filterByViewerTeams(
        (response1?.data || []).flatMap((team: any) => {
          const subTeams =
            team.sub_teams?.map((subTeam: any) => {
              const tid = String(subTeam._id);
              const isAssigned =
                isFlagOn(subTeam?.is_available) || assignedTeamIds.has(tid);
              return {
                id: subTeam._id,
                label: subTeam.team_name,
                selected: isAssigned ? 1 : 0,
                isParent: false,
                teamMembers: subTeam.team_members?.length
                  ? subTeam.team_members.map((member: any) => ({
                      id: member.userId,
                      label: member.users?.[0]?.name,
                      selected: 0,
                    }))
                  : null,
              };
            }) || [];

          const tid = String(team._id);
          const mainAssigned =
            isFlagOn(team?.is_available) || assignedTeamIds.has(tid);
          // Parent teams are also selectable so already-assigned parents show in chips
          const mainTeam = {
            id: team._id,
            label: team.team_name,
            selected: mainAssigned ? 1 : 0,
            isParent: false,
            teamMembers: team.team_members?.length
              ? team.team_members.map((member: any) => ({
                  id: member.userId,
                  label: member.users?.[0]?.name,
                  selected: 0,
                }))
              : null,
          };
          return [mainTeam, ...subTeams];
        }),
        viewerTeamIds
      );

      // Seed missing assigned teams from lead.AssignTo objects
      const optionIds = new Set(assignToData.map((e: any) => String(e.id)));
      if (Array.isArray(row.AssignTo)) {
        row.AssignTo.forEach((t: any) => {
          const tid = String(t?.team_id || t?._id || t?.id || "").replace(
            /['"]/g,
            ""
          );
          const label = t?.team_name || t?.label || tid;
          if (tid && !optionIds.has(tid)) {
            assignToData.push({
              id: tid,
              label,
              selected: 1,
              isParent: false,
              teamMembers: null,
            });
            optionIds.add(tid);
          } else if (tid) {
            const idx = assignToData.findIndex(
              (e: any) => String(e.id) === tid
            );
            if (idx >= 0) assignToData[idx].selected = 1;
          }
        });
      } else {
        assignedTeamIds.forEach((tid) => {
          const idx = assignToData.findIndex((e: any) => String(e.id) === tid);
          if (idx >= 0) {
            assignToData[idx].selected = 1;
          } else if (tid) {
            assignToData.push({
              id: tid,
              label: tid,
              selected: 1,
              isParent: false,
              teamMembers: null,
            });
          }
        });
      }

      setLeadOption(assignToData);

      const selectedData = assignToData
        .filter((e: any) => e?.selected == 1)
        .map((e: any) => ({
          id: e.id,
          label: e.label,
          selected: 1,
          isParent: false,
          teamMembers: Array.isArray(e.teamMembers)
            ? e.teamMembers.map((member: any) => ({
                id: member.id,
                label: member.label,
                selected: 0,
              }))
            : null,
        }));

      setSelectedOptions(selectedData);

      // Seed members immediately from lead so UI shows even before members API
      const seededMembers = Array.isArray(leadMembersForSelect)
        ? leadMembersForSelect.map((m: any) => ({
            pId: "",
            id: cleanId(m?.user_id || m?.userId || m?._id || m?.id),
            label: m?.name || m?.fullName || m?.userName || "Member",
            selected: 1,
          })).filter((m: any) => m.id)
        : parseAssignedMemberIds(leadMembersForSelect).map((uid) => ({
            pId: "",
            id: uid,
            label: uid,
            selected: 1,
          }));

      // Dedupe seeded members by id
      const uniqueSeeded = Array.from(
        new Map(seededMembers.map((m: any) => [String(m.id), m])).values()
      );

      if (uniqueSeeded.length) {
        setLeadOptionMembersas(uniqueSeeded);
        setSelectedOptionsMembers(uniqueSeeded);
      } else {
        setLeadOptionMembersas([]);
        setSelectedOptionsMembers([]);
      }

      // Seed Admin / Super Admin / TL into member list even with zero teams selected
      const auth = store.getState().auth || {};
      const isAdminUser = canAssignToSelf(auth);
      if (isAdminUser && auth.userId) {
        const meId = cleanId(auth.userId);
        const meOpt = {
          pId: "",
          id: meId,
          label: auth.userName
            ? `${auth.userName} (Me)`
            : "Assign to me",
          selected: assignedMemberIds.has(meId) ? 1 : 0,
          isAdmin: true,
        };
        setLeadOptionMembersas((prev: any) => {
          const list = Array.isArray(prev) ? [...prev] : [];
          if (!list.some((m: any) => String(m.id) === meId)) {
            list.unshift(meOpt);
          }
          return list;
        });
        if (meOpt.selected) {
          setSelectedOptionsMembers((prev: any) => {
            const list = Array.isArray(prev) ? [...prev] : [];
            if (!list.some((m: any) => String(m.id) === meId)) {
              list.push(meOpt);
            }
            return list;
          });
        }
      }

      const ids = selectedData.map((e: any) => e.id);
      if (ids.length) {
        await handelDropdownAfterSelect(ids, id, assignedMemberIds);
      }
      setLoading(false);
    } catch {
      setLoading(false);
    } finally {
      setApiResponse(false);
      setLoading(false);
    }
  };

  const openCustomer = (params: any) => {
    const lead = params?.row || params;
    const leadId = lead?._id || params?._id;
    if (!leadId) return;
    try {
      sessionStorage.setItem(`all-lead-details-${leadId}`, JSON.stringify(lead));
      const navList = (userDataSource1 || [])
        .filter((item: any) => item?._id)
        .map((item: any) => ({ ...item }));
      sessionStorage.setItem("all-leads-nav-list", JSON.stringify(navList));
    } catch {
      // ignore storage failures
    }
    const returnPath = withListPagination(
      urls.ALLLEADFORMLIST,
      paginationModel.page,
      paginationModel.pageSize,
      location.search
    );
    setLeadListReturnPath(returnPath);
    history.push(urls.ALLLEADDETAILS.replace(":id", leadId), {
      lead,
      type: params?.type || "",
      from: returnPath,
    });
  };

  const handleEdit = (id: string) => {
    const row =
      userDataSource1?.find((r: any) => r._id === id) ||
      (selectedRow?._id === id ? selectedRow : null) ||
      (eventForEdit && (eventForEdit as any)?._id === id
        ? eventForEdit
        : null);
    if (row) {
      sessionStorage.setItem(`all-lead-edit-${id}`, JSON.stringify(row));
    }
    setUserId(id);
    history.push(urls.EDITLEADPAGE.replace(":id", id));
  };

  // const handleCloseLoan = () => {
  //   setIsDialogOpenLoan(false);
  // };

  // const handleCloseLoanList = () => {
  //   setIsDialogOpenLoanList(false);
  // };

  const handlePopOver = (
    event: React.MouseEvent<HTMLButtonElement>,
    user_id: string,
    status: any,
    userData: any
  ) => {
    setAnchorEl(event.currentTarget);
    setUserRow_Id(user_id);
    setRowStatus(status);
    setEventForEdit(userData);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleCloseModal = () => {
    setOpenModal({
      name: "",
      data: {},
      open: false,
    });
  };

  const getReminderInfo = async (id: any) => {
    try {
      setApiResponse(true);
      setLoading(true);
      const response = await getReminder(id);

      if (response.success == 1) {
        setResponseValuereminder((prevFormData: any) => ({
          ...prevFormData,
          value: response?.data,
        }));
      } else {
        setResponseValuereminder([]);
      }
      setLoading(false);
    } catch (error: any) {
      // openErrorNotification(
      //   isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      // );
      setLoading(false);
    } finally {
      setApiResponse(false);
      setLoading(false);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const handleRowClick = async (id: any) => {
    setShowSideBar(!showSideBar);
    setDynamicGridValue(id.row);
    setaddinationalValue(id?.row.dynamicFields || []);
    handelDropdown(id);
    getRemarkInfo(id?.id);
    getReminderInfo(id?.id);
  };

  const renderMenu = () => {
    if (!menuPosition) return null;
    return (
      <>
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setMenuPosition(null);
            setSelectedRow(null);
          }}
        />
        <div
          className="fixed z-50 w-[220px] max-w-full rounded-yh-md border border-yh-border bg-white py-1 shadow-yh"
          style={{
            top: menuPosition.top,
            left: Math.max(0, menuPosition.left - 190),
          }}
        >
          <div className="cursor-default px-3 py-2 text-xs font-yh-bold text-yh-light-gray">
            Actions
          </div>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-yh-black hover:bg-yh-lilac"
            onClick={() => {
              if (selectedRow?._id) {
                setEventForEdit(selectedRow);
                handleEdit(selectedRow._id);
              }
              setMenuPosition(null);
            }}
          >
            <EditSvg />
            Edit
          </button>
          {recycleMode || isNiOrLostStage(selectedRow?.stage) ? (
            <>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-yh-black hover:bg-yh-lilac"
                onClick={async () => {
                  const row = selectedRow;
                  setMenuPosition(null);
                  if (!row?._id) return;
                  const names = (stageOptions || [])
                    .map((o: any) => String(o?.label || "").trim())
                    .filter(Boolean);
                  const reopen = defaultReopenStage(names);
                  await applyStageChange(row, reopen);
                }}
              >
                Reopen
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-yh-black hover:bg-yh-lilac"
                onClick={() => {
                  const row = selectedRow;
                  setMenuPosition(null);
                  if (!row?._id) return;
                  setSelectedLeadIds([String(row._id)]);
                  setBulkAssignOpen(true);
                }}
              >
                Reassign
              </button>
            </>
          ) : null}
        </div>
      </>
    );
  };

  // Bulk reminders (GET /getLeadReminder once) → map by leadId for the Reminder column.
  // Do NOT call getLeadReminder/:id per table row (N+1 floods live).
  useEffect(() => {
    const map: Record<string, any[]> = {};
    (remData || []).forEach((r: any) => {
      const leadId = String(r?.leadId || "").trim();
      if (!leadId || leadId === "NA") return;
      if (!map[leadId]) map[leadId] = [];
      map[leadId].push(r);
    });
    setReminders(map);
  }, [remData]);

  const pageIds = useMemo(
    () =>
      (userDataSource1 || [])
        .map((r: any) => String(r?._id || ""))
        .filter(Boolean),
    [userDataSource1]
  );
  const matchingTotal = Number(metaData?.total || 0);
  const allPageSelected =
    pageIds.length > 0 &&
    pageIds.every((id: string) => selectedLeadIds.includes(id));
  const selectedCount = selectAllMatching
    ? matchingTotal || selectedLeadIds.length
    : selectedLeadIds.length;

  const clearLeadSelection = () => {
    setSelectAllMatching(false);
    setSelectedLeadIds([]);
  };

  const filterPayloadForIds = () => {
    const stageFilter = expandStageSelection(selectedStages, allSatgedata);
    return {
    userId: store.getState().auth.userId,
    search: search || "",
    stage: stageFilter.length ? stageFilter.join(",") : "",
    source: selectedSources.length ? selectedSources.join(",") : "",
    formId: formLead.length > 0 ? formLead.join(",") : "",
    startDate:
      freshFilter || !selectedDateRange?.from ? "" : selectedDateRange.from,
    endDate:
      freshFilter || !selectedDateRange?.from
        ? ""
        : selectedDateRange.to || selectedDateRange.from,
    assignment: assignmentFilter === "all" ? "" : assignmentFilter,
    uploadLeadId: selectedUploadLeadIds.length
      ? selectedUploadLeadIds.join(",")
      : "",
    memberId: selectedMemberIds.length
      ? selectedMemberIds.join(",")
      : "",
    team_id: !selectedMemberIds.length && selectedTeamIds.length
      ? selectedTeamIds.join(",")
      : "",
    fresh: freshFilter ? "1" : "",
    organizationId:
      activeOrganizationId &&
      activeOrganizationId !== "all" &&
      activeOrganizationId !== "*"
        ? String(activeOrganizationId)
        : "",
  };
  };

  const toggleLeadSelect = (id: string, checked: boolean) => {
    setSelectAllMatching(false);
    setSelectedLeadIds((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }
      return prev.filter((x) => x !== id);
    });
  };

  const toggleSelectAllPage = (checked: boolean) => {
    if (!checked) {
      clearLeadSelection();
      return;
    }
    setSelectAllMatching(false);
    setSelectedLeadIds((prev) => {
      const set = new Set([...prev, ...pageIds]);
      return Array.from(set);
    });
  };

  /** Select every lead matching current filters across all pages */
  const selectAllPagesMatching = async () => {
    if (!matchingTotal) {
      openErrorNotification("No leads match the current filters");
      return;
    }
    setSelectAllLoading(true);
    try {
      const res = await getAllMatchingLeadIds(filterPayloadForIds());
      const ids = (Array.isArray(res?.data) ? res.data : [])
        .map((r: any) => String(r?._id || ""))
        .filter(Boolean);
      if (!ids.length) {
        openErrorNotification("No matching leads found");
        return;
      }
      const capped = Boolean(res?.metadata?.[0]?.capped);
      setSelectAllMatching(true);
      setSelectedLeadIds(ids);
      openSuccessNotification(
        capped
          ? `Selected first ${ids.length} of ${matchingTotal} matching leads (max 20,000)`
          : `Selected all ${ids.length} matching lead(s) across pages`
      );
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to select all matching leads");
    } finally {
      setSelectAllLoading(false);
    }
  };

  /** Select every lead on the current page that matches the source filter (or all if no filter). */
  const selectSourceLeadsOnPage = () => {
    setSelectAllMatching(false);
    const ids = (userDataSource1 || [])
      .filter((r: any) => {
        if (!selectedSources.length) return true;
        const rowSrc = String(r?.source || "").toLowerCase();
        return selectedSources.some(
          (s) => String(s).toLowerCase() === rowSrc
        );
      })
      .map((r: any) => String(r?._id || ""))
      .filter(Boolean);
    if (!ids.length) {
      openErrorNotification(
        selectedSources.length
          ? `No ${selectedSources.join(", ")} leads on this page`
          : "No leads on this page"
      );
      return;
    }
    setSelectedLeadIds((prev) => Array.from(new Set([...prev, ...ids])));
    openSuccessNotification(
      selectedSources.length
        ? `Selected ${ids.length} ${selectedSources.join(", ")} lead(s) on this page`
        : `Selected ${ids.length} lead(s) on this page`
    );
  };

  const columns: YhTableColumn[] = [
    {
      headerName: (
        <input
          type="checkbox"
          checked={selectAllMatching || allPageSelected}
          onChange={(e) => toggleSelectAllPage(e.target.checked)}
          aria-label="Select all on page"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      field: "_select",
      minWidth: 52,
      width: 52,
      renderCell: (params: YhTableCellParams) => {
        const id = String(params.row?._id || "");
        return (
          <input
            type="checkbox"
            className="action-button"
            data-action-button="true"
            checked={selectAllMatching || selectedLeadIds.includes(id)}
            onChange={(e) => {
              e.stopPropagation();
              toggleLeadSelect(id, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label="Select lead"
          />
        );
      },
    },
    {
      headerName: "Lead",
      field: "leadName",
      minWidth: 220,
      renderCell: (params: YhTableCellParams) => {
        const name = params.row?.leadName || "?";
        const initial = String(name).trim().charAt(0).toUpperCase() || "?";
        const avatarPalette = [
          { bg: "#F9F0F4", color: "#6B1630" }, // purple
          { bg: "#DBEAFE", color: "#2563EB" }, // blue
          { bg: "#D1FAE5", color: "#059669" }, // green
          { bg: "#FFEDD5", color: "#EA580C" }, // orange
          { bg: "#FEF3C7", color: "#D97706" }, // amber
          { bg: "#FCE7F3", color: "#DB2777" }, // pink
          { bg: "#CCFBF1", color: "#0D9488" }, // teal
        ];
        let hash = 0;
        for (let i = 0; i < String(name).length; i++) {
          hash = String(name).charCodeAt(i) + ((hash << 5) - hash);
        }
        const avatar = avatarPalette[Math.abs(hash) % avatarPalette.length];

        return (
          <div
            className="action-button flex cursor-pointer items-center gap-3"
            data-action-button="true"
            onClick={(e) => {
              e.stopPropagation();
              openCustomer(params.row);
              setLeadData(params.row);
            }}
          >
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-bold"
              style={{ backgroundColor: avatar.bg, color: avatar.color }}
            >
              {initial}
            </span>
            <div className="flex min-w-0 flex-col items-start gap-0.5">
              <span className="truncate text-sm font-semibold capitalize text-[#A02448]">
                {name}
              </span>
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="shrink-0 text-xs text-[#9CA3AF]">
                  {params.row?.uid || "—"}
                </span>
                {(() => {
                  const arrival = leadActivityTime(params.row);
                  const age = relativeLeadTag(arrival);
                  const fresh = isFreshLeadToday(params.row);
                  if (!fresh && !age) return null;
                  return (
                    <>
                      {fresh ? (
                        <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                          Fresh
                        </span>
                      ) : null}
                      {age ? (
                        <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700">
                          {age}
                        </span>
                      ) : null}
                    </>
                  );
                })()}
                {Array.isArray(params.row?.notInterestedBy) &&
                params.row.notInterestedBy.length > 0 ? (
                  <span
                    className="max-w-[160px] truncate rounded-full bg-[#FEF2F2] px-2 py-0.5 text-[10px] font-semibold text-[#B91C1C]"
                    title={`Marked Not Interested by ${params.row.notInterestedBy
                      .map((u: any) => u?.name || u?.user_name || u)
                      .filter(Boolean)
                      .join(", ")}`}
                  >
                    Not Interested by{" "}
                    {params.row.notInterestedBy
                      .map((u: any) => u?.name || u?.user_name || String(u))
                      .filter(Boolean)
                      .slice(0, 2)
                      .join(", ")}
                    {params.row.notInterestedBy.length > 2 ? "…" : ""}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      headerName: "Form name",
      field: "form_name",
      minWidth: 180,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex min-w-0 flex-col items-start gap-0.5">
          <span className="truncate text-sm font-semibold capitalize text-[#111827]">
            {params.row.form_name || "—"}
          </span>
          
        </div>
      ),
    },
    {
      headerName: "Project name",
      field: "project_name",
      minWidth: 180,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex min-w-0 flex-col items-start gap-0.5">
          <span className="truncate text-sm font-semibold capitalize text-[#111827]">
            {params.row.project_name || "—"}
          </span>
          <span className="truncate text-xs capitalize text-[#6B7280]">
            {params.row.developer_name || ""}
          </span>
        </div>
      ),
    },
    {
      headerName: "Organization",
      field: "organizationId",
      minWidth: 160,
      renderCell: (params: YhTableCellParams) => (
        <OrgNameCell
          organizationId={params.row?.organizationId}
          map={orgNameMap}
          fallbackLabel={params.row?.organizationName}
        />
      ),
    },
    {
      headerName: "Mobile",
      field: "leadPhone",
      minWidth: 160,
      renderCell: (params: YhTableCellParams) => {
        const display = formatPhoneKeepingCountryCode(
          resolveLeadPhoneForDisplay(params.row)
        );
        return (
          <span
            className="font-mono text-sm tracking-wide text-[#374151]"
            title={display}
          >
            {display}
          </span>
        );
      },
    },
    ...(showPaymentColumn
      ? [
          {
            headerName: "Payment",
            field: "paymentStatus",
            minWidth: 128,
            width: 128,
            renderCell: (params: YhTableCellParams) => {
        const row = params.row;
        const rawStatus = String(
          row?.paymentStatus ||
            row?.easebuzzStatus ||
            row?.payment?.paymentStatus ||
            row?.payment?.easebuzzStatus ||
            ""
        ).trim();
        const status = rawStatus.toLowerCase();
        const amount =
          row?.paymentAmount ||
          row?.amount ||
          row?.payment?.amount ||
          "";

        if (!status && !amount) {
          return (
            <div className="w-[96px] text-left text-sm text-[#9CA3AF]">—</div>
          );
        }

        let badgeStyle = "bg-[#F3F4F6] text-[#4B5563]";
        let label = rawStatus || "—";

        if (status === "paid" || status === "success" || status === "successful") {
          badgeStyle = "bg-[#DCFCE7] text-[#166534]";
          label = "Paid";
        } else if (
          status === "pending" ||
          status === "pre-initiated" ||
          status === "preinitiated"
        ) {
          badgeStyle = "bg-[#FEF3C7] text-[#92400E]";
          label = "Pending";
        } else if (status === "unpaid") {
          badgeStyle = "bg-[#F9F0F4] text-[#3730A3]";
          label = "Unpaid";
        } else if (status === "failed" || status === "failure") {
          badgeStyle = "bg-[#FEE2E2] text-[#991B1B]";
          label = "Failed";
        } else if (
          status === "bounced" ||
          status === "dropped" ||
          status === "cancelled"
        ) {
          badgeStyle = "bg-[#DBEAFE] text-[#1E40AF]";
          label = status.charAt(0).toUpperCase() + status.slice(1);
        } else if (rawStatus) {
          label = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);
        }

        return (
          <div className="flex w-[96px] flex-col items-start gap-0.5">
            {status ? (
              <span
                className={`inline-flex h-6 w-full items-center justify-center gap-1 rounded-full px-2 text-[11px] font-bold leading-none ${badgeStyle}`}
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-80" />
                {label}
              </span>
            ) : null}
            {amount ? (
              <span className="w-full font-mono text-xs font-semibold tabular-nums text-[#374151]">
                ₹{Number(amount).toLocaleString("en-IN")}
              </span>
            ) : null}
          </div>
        );
      },
          },
        ]
      : []),
    {
      headerName: "Channel Partner",
      field: "channelPartnerName",
      minWidth: 150,
      renderCell: (params: YhTableCellParams) => {
        const name =
          params.row?.channelPartnerName ||
          (params.row?.isFromChannelPartner ? "Channel Partner" : "");
        if (!name && !params.row?.channelPartnerId) {
          return <span className="text-sm text-[#9CA3AF]">—</span>;
        }
        return (
          <span
            className="truncate text-sm font-medium text-[#111827]"
            title={name || String(params.row?.channelPartnerId || "")}
          >
            {name || "Linked CP"}
          </span>
        );
      },
    },
    {
      headerName: "Reminder",
      field: "reminder",
      minWidth: 120,
      renderCell: (params: YhTableCellParams) => {
        const leadId = params.row._id;
        const reminderData = reminders[leadId];

        if (reminderData && reminderData.length > 0) {
          const latestReminder = reminderData.reduce(
            (
              latest: { date: any; time: any },
              current: { date: any; time: any }
            ) => {
              const latestDateTime = new Date(`${latest.date}T${latest.time}`);
              const currentDateTime = new Date(
                `${current.date}T${current.time}`
              );
              return currentDateTime > latestDateTime ? current : latest;
            }
          );

          const reminderDateTimeString = `${latestReminder.date}T${latestReminder.time}`;
          const reminderDateTime = new Date(reminderDateTimeString);
          const currentDate = new Date();
          const isExpired = reminderDateTime < currentDate;

          return (
            <div className="flex flex-col items-start gap-1">
              {!isExpired ? (
                <span className="inline-flex h-7 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 text-[11px] font-semibold text-emerald-600">
                  Reminder
                </span>
              ) : (
                <span className="inline-flex h-7 items-center justify-center rounded-full border border-red-200 bg-red-50 px-2.5 text-[11px] font-semibold text-red-600">
                  Expired
                </span>
              )}
            </div>
          );
        } else {
          return <div className="h-7 w-[70px]" />;
        }
      },
    },
    {
      headerName: "Source",
      field: "source",
      minWidth: 150,
      renderCell: (params: YhTableCellParams) => {
        const source = params.row?.source || "—";
        return (
          <div className="flex items-center gap-2.5">
            <YhSourceIcon source={source} />
            <span className="truncate text-sm font-medium capitalize text-[#374151]">
              {source}
            </span>
          </div>
        );
      },
    },
    {
      headerName: "Stage",
      field: "stage",
      minWidth: 130,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          className="action-button inline-flex h-8 items-center rounded-full border border-orange-300 bg-orange-50 px-3 text-xs font-semibold capitalize text-orange-600 transition hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            setOpenModal({
              name: "Stage Details",
              data: {
                stage: params?.row?.AssignToUserStage,
                members: params.row?.AssignToUser,
                leadStage: params.row?.stage || "Stages",
                leadName: params.row?.leadName,
                assignmentMode: params.row?.assignmentMode || "separate",
              },
              open: true,
            });
          }}
          data-action-button="true"
          disabled={
            !params?.row?.AssignToUserStage?.length && !params?.row?.stage
          }
        >
          {params.row?.stage || "Initial"}
        </button>
      ),
    },
    {
      headerName: "Assigned To",
      field: "at",
      minWidth: 150,
      renderCell: (params: YhTableCellParams) => {
        const teams = Array.isArray(params.row?.AssignTo)
          ? params.row.AssignTo
          : parseAssignedTeamIds(params.row?.AssignTo).map((id) => ({
              _id: id,
              team_name: id,
            }));
        const membersRaw = Array.isArray(params.row?.AssignToUser)
          ? params.row.AssignToUser
          : parseAssignedMemberIds(params.row?.AssignToUser);
        const members = (membersRaw || []).flatMap((m: any) => {
          if (typeof m === "string" && m.includes(",")) {
            return m.split(",").map((id: string) => ({
              _id: cleanId(id),
              name: cleanId(id),
            }));
          }
          if (typeof m === "string" || typeof m === "number") {
            return [{ _id: cleanId(m), name: cleanId(m) }];
          }
          return [m];
        });
        const displayCount = members.length || teams.length || 0;
        const shown = members.slice(0, 2);
        return (
          <button
            type="button"
            className="action-button flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
              setOpenModal({
                name: "Assigne-To",
                data: {
                  team: params.row?.AssignTo,
                  members: params.row?.AssignToUser,
                },
                open: true,
              });
            }}
            data-action-button="true"
            disabled={!displayCount}
          >
            <span className="flex h-7 min-w-[28px] items-center justify-center rounded-full bg-[#F9F0F4] px-1.5 text-xs font-bold text-[#A02448]">
              {displayCount}
            </span>
            <span className="flex -space-x-2">
              {shown.map((m: any, i: number) => {
                const label =
                  m?.fullName || m?.name || m?.userName || m?.email || "?";
                const ch = String(label).charAt(0).toUpperCase();
                return (
                  <span
                    key={m?._id || m?.id || i}
                    className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold text-white"
                    style={{
                      backgroundColor: ["#A02448", "#14B8A6", "#F59E0B"][i % 3],
                    }}
                    title={label}
                  >
                    {ch}
                  </span>
                );
              })}
              {!shown.length && displayCount > 0 ? (
                <span className="text-xs font-medium text-[#6B7280]">
                  {displayCount} assigned
                </span>
              ) : null}
            </span>
          </button>
        );
      },
    },
    {
      headerName: "Created Date",
      field: "createdAt",
      minWidth: 130,
      renderCell: (params) => {
        const parts = formatLeadDateParts(leadActivityTime(params.row));
        if (!parts) {
          return <span className="text-xs text-[#9CA3AF]">—</span>;
        }
        return (
          <div className="flex flex-col gap-0.5 text-xs leading-snug text-[#6B7280]">
            <div className="font-medium text-[#111827]">{parts.date}</div>
            <div>{parts.time}</div>
          </div>
        );
      },
    },
    {
      headerName: "Updated Date",
      field: "updatedAt",
      minWidth: 130,
      renderCell: (params) => {
        const parts = formatLeadDateParts(params.value, [
          params.row?.createdAt,
        ]);
        if (!parts) {
          return <span className="text-xs text-[#9CA3AF]">—</span>;
        }
        return (
          <div className="flex flex-col gap-0.5 text-xs leading-snug text-[#6B7280]">
            <div className="font-medium text-[#111827]">{parts.date}</div>
            <div>{parts.time}</div>
          </div>
        );
      },
    },
    {
      headerName: "Action",
      field: "action",
      minWidth: 80,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          className="action-button flex h-8 w-8 items-center justify-center rounded-xl text-[#6B7280] transition-colors hover:bg-[#F3F4F6] hover:text-[#111827]"
          onClick={(event) => {
            event.stopPropagation();
            const rect = event.currentTarget.getBoundingClientRect();
            setMenuPosition({ top: rect.bottom, left: rect.left });
            setSelectedRow(params.row);
            loadLeadCatalogs();
          }}
          data-action-button="true"
        >
          <VerticalDotIcon />
        </button>
      ),
    },
  ];

  const patchLeadStageInPlace = (
    leadId: string,
    stage: string,
    extras?: { AssignToUser?: any[] }
  ) => {
    const uid = String(store.getState().auth?.userId || "");
    const apply = (row: any) =>
      String(row?._id) === String(leadId)
        ? patchLeadStageOnRow(row, stage, uid, extras)
        : row;
    setUserDataSource1((prev: any[]) => (prev || []).map(apply));
    setSelectedRow((prev: any) => (prev ? apply(prev) : prev));
  };

  const applyStageChange = async (
    data: any,
    value: string,
    reason?: string
  ) => {
    if (!value || String(value) === String(data?.stage || "")) return;
    const body: any = {
      id: data?._id,
      stage: value,
    };
    if (reason) {
      body.reason = reason;
      body.stageReason = reason;
    }
    try {
      setApiResponse(true);
      const response = await changeStage(body);
      if (response.success === 1) {
        patchLeadStageInPlace(String(data?._id), value, {
          AssignToUser: response?.AssignToUser,
        });
        setShowSideBar(showSideBar);
        const converted =
          response?.customerConverted ||
          String(value).trim().toLowerCase() === "customer";
        openSuccessNotification(
          converted
            ? response?.message || "Lead converted to customer successfully"
            : response?.message
        );
      } else {
        openErrorNotification(
          response?.message || "Failed to update stage"
        );
      }
      setApiResponse(false);
    } catch (error: any) {
      setApiResponse(false);
      openErrorNotification(error.message || notifiers.GENERIC_ERROR);
    }
  };

  const [stageReason, setStageReason] = useState<{
    open: boolean;
    stage: string;
    lead: any;
  }>({ open: false, stage: "", lead: null });
  const [savingStageReason, setSavingStageReason] = useState(false);
  const [recycleMode, setRecycleMode] = useState(false);

  const confirmStageReason = async (reason: string) => {
    const lead = stageReason.lead;
    if (!lead?._id || !stageReason.stage) return;
    try {
      setSavingStageReason(true);
      await applyStageChange(lead, stageReason.stage, reason);
      setStageReason({ open: false, stage: "", lead: null });
    } catch (error: any) {
      openErrorNotification(error?.message || notifiers.GENERIC_ERROR);
    } finally {
      setSavingStageReason(false);
    }
  };

  const confirmConvertCustomer = async () => {
    if (!customerConfirm.lead?._id || !customerConfirm.stage) return;
    try {
      setConvertingCustomer(true);
      await applyStageChange(customerConfirm.lead, customerConfirm.stage);
      setCustomerConfirm({ open: false, stage: "", lead: null });
    } finally {
      setConvertingCustomer(false);
    }
  };

  const handleAutocompleteChange = async (
    name: string,
    value: any,
    data: any,
    opts?: { reasonAlreadySaved?: boolean; reason?: string }
  ) => {
    if (!name) return;

    switch (name) {
      case "stage": {
        if (String(value || "").trim().toLowerCase() === "customer") {
          setCustomerConfirm({ open: true, stage: value, lead: data });
          break;
        }
        if (isNiOrLostStage(value) && !opts?.reasonAlreadySaved) {
          setStageReason({ open: true, stage: value, lead: data });
          break;
        }
        await applyStageChange(data, value, opts?.reason);
        break;
      }

      case "assignTo": {
        const body = { id: data?._id, AssignTo: value?.id };
        try {
          setApiResponse(true);
          await changeAt(body);
          tableDataShowHandler();
        } catch (error: any) {
          setApiResponse(false);
          openErrorNotification(error.message || notifiers.GENERIC_ERROR);
        }
        break;
      }

      case "formName": {
        if (!value || value.label === "All") {
          tableDataShowHandler();
          return;
        }

        const filteredData = response.filter((e: any) => e.formId === value.id);

        const transformedData = filteredData.map((userData: any) => ({
          lead: (
            <>
              <span className="font-yh-bold text-xs text-yh-black">{userData.uid}</span>
              <span className="font-yh-bold text-xs text-yh-black">
                {userData.leadName}
              </span>
            </>
          ),
          // reminder: <>{getReminderInfo(userData._id)}</>,
          property: (
            <span className="text-[11px] text-yh-black">
              {userData.projecttypeId}
            </span>
          ),
          contact: (
            <span className="text-[11px] text-yh-black">
              {userData.leadEmail}, {userData.leadPhone}
            </span>
          ),
          formName: (
            <span className="text-[11px] text-yh-black">
              {userData.form_name}
            </span>
          ),
          source: (
            <span className="text-[11px] text-yh-black">
              {userData.source}
            </span>
          ),
          stage: (
            <select
              className="yh-input h-9 text-xs"
              value={userData.stage || ""}
              onChange={(event) =>
                handleAutocompleteChange("stage", event.target.value, userData)
              }
            >
              <option value="" disabled>
                Select stage
              </option>
              {stageData.map((s: string) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          ),
          at: (
            <>
              {(userData.AssignTo || [])?.map((item: any) => (
                <span key={item.team_id} className="block text-xs text-yh-black">{item.team_name}</span>
              ))}
              {(userData.AssignToUser || [])?.map((item: any) => (
                <span key={item.user_id} className="block text-xs text-yh-black">{item.name}</span>
              ))}
            </>
          ),
          action: (
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-yh-md text-yh-black hover:bg-yh-lilac"
              onClick={(event) =>
                handlePopOver(
                  event,
                  userData.formId,
                  userData.status,
                  userData
                )
              }
            >
              <VerticalDotIcon />
            </button>
          ),
        }));
        setUserDataSource(transformedData);
        setUserDataSource1(filteredData);

        setApiResponse(false);
        setLoading(false);
        break;
      }

      case "source": {
        if (!canEditLeadSource(store.getState().auth || {})) {
          openErrorNotification("Only Admin can edit lead source");
          break;
        }
        try {
          setApiResponse(true);
          const body = {
            id: data?._id,
            formId: data.formId,
            form_name: data.form_name,
            developerId: data.developerId,
            projectId: data.projectId,
            projecttypeId: data.projecttypeId,
            leadName: data.leadName,
            leadEmail: data.leadEmail,
            leadPhone: data.leadPhone,
            dynamicFields: data.dynamicFields || [],
            AssignTo: data.AssignTo,
            AssignToUser: data.AssignToUser,
            stage: data.stage || "NA",
            status: data.status,
            source: value,
          };
          const response = await updatePersonalDetail(body);
          if (response?.success) {
            setDynamicGridValue((prev: any) =>
              prev ? { ...prev, source: value } : prev
            );
            openSuccessNotification(
              response?.message || "Source updated successfully"
            );
            tableDataShowHandler();
          } else {
            openErrorNotification(
              response?.message || "Failed to update source"
            );
          }
          setApiResponse(false);
        } catch (error: any) {
          setApiResponse(false);
          openErrorNotification(error.message || notifiers.GENERIC_ERROR);
        }
        break;
      }

      default:
        console.warn("Unhandled case in handleAutocompleteChange");
    }
  };
  const handelSideSave = async (body: any) => {
    try {
      setApiResponse(true);
      const response = await saveReminder(body);
      if (response.success === 1) {
        const response5 = await getReminderData();
        const list = Array.isArray(response5?.data) ? response5.data : [];
        setRemData(list);
        // remData effect rebuilds reminders map; also patch this lead for instant UI
        if (body.leadId) {
          setReminders((prev) => ({
            ...prev,
            [body.leadId]: list.filter(
              (r: any) => String(r?.leadId) === String(body.leadId)
            ),
          }));
        }
        tableDataShowHandler();
      }
    } catch (error: any) {
      setApiResponse(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };
  const handelAssignToUser = async (dynamicGridValue: any) => {
    try {
      const leadId =
        dynamicGridValue?._id ||
        dynamicGridValue?.row?._id ||
        dynamicGridValue?.id;
      if (!leadId) {
        openErrorNotification("Lead not found");
        return;
      }

      const teamIds = (selectedOptions || [])
        .map((item: any) => String(item.id ?? "").replace(/['"]/g, "").trim())
        .filter(Boolean);
      const teamMemberIds = new Set(
        (leadOptionMembers || []).map((member: any) =>
          String(member?.id ?? "").replace(/['"]/g, "").trim()
        )
      );
      const memberIds = (selectedOptionsMembers || [])
        .map((item: any) => String(item.id ?? "").replace(/['"]/g, "").trim())
        .filter((id: string) => {
          if (!id) return false;
          const picked = (selectedOptionsMembers || []).find(
            (item: any) => String(item.id ?? "").replace(/['"]/g, "").trim() === id
          );
          if (picked?.isAdmin) return true;
          return !teamIds.length || teamMemberIds.has(id);
        });

      if (!teamIds.length && !memberIds.length) {
        openErrorNotification("Select a team or member to assign");
        return;
      }

      if (teamIds.length) {
        const idTeamVal = teamIds.map((tid: string) => `'${tid}'`).join(",");
        const teamRes = await changeAt({
          id: leadId,
          AssignTo: idTeamVal,
        });
        if (!(teamRes?.success === 1 || teamRes?.success === true)) {
          openErrorNotification(teamRes?.message || "Failed to assign team");
          return;
        }
      }

      if (memberIds.length) {
        const response = await assignToMembers({
          id: leadId,
          AssignToUser: memberIds.join(","),
          AssignTo: teamIds.length
            ? teamIds.map((tid: string) => `'${tid}'`).join(",")
            : undefined,
          stage: dynamicGridValue?.stage || dynamicGridValue?.row?.stage || "Initial",
        });
        if (!(response?.success === 1 || response?.success === true)) {
          openErrorNotification(
            response?.message || "Failed to assign members"
          );
          return;
        }
        openSuccessNotification("Lead assigned successfully");
      } else {
        openSuccessNotification("Team assigned successfully");
      }

      setIdForMember([]);
      setIdForTeam([]);
      tableDataShowHandler();
    } catch (error: any) {
      openErrorNotification(error?.message || "Failed to assign");
    }
  };

  const handleAssignToMe = async (dynamicGridValue: any) => {
    try {
      const auth = store.getState().auth || {};
      if (!canAssignToSelf(auth)) {
        openErrorNotification(
          "Only Admin, Super Admin, or Team Lead can assign to self"
        );
        return;
      }
      const meId = cleanId(auth.userId);
      if (!meId) {
        openErrorNotification("Not signed in");
        return;
      }
      const leadId =
        dynamicGridValue?._id ||
        dynamicGridValue?.row?._id ||
        dynamicGridValue?.id;
      if (!leadId) {
        openErrorNotification("Lead not found");
        return;
      }
      const meOpt = {
        id: meId,
        label: auth.userName ? `${auth.userName} (Me)` : "Assign to me",
        selected: 1,
        isAdmin: true,
      };
      setSelectedOptionsMembers([meOpt]);
      setLeadOptionMembersas((prev: any) => {
        const list = Array.isArray(prev) ? [...prev] : [];
        if (!list.some((m: any) => String(m.id) === meId)) {
          list.unshift({ ...meOpt, pId: "" });
        }
        return list;
      });
      const response = await assignToMembers({
        id: leadId,
        AssignToUser: meId,
        stage:
          dynamicGridValue?.stage ||
          dynamicGridValue?.row?.stage ||
          "Initial",
      });
      if (!(response?.success === 1 || response?.success === true)) {
        openErrorNotification(response?.message || "Failed to assign to you");
        return;
      }
      openSuccessNotification("Lead assigned to you");
      tableDataShowHandler();
    } catch (error: any) {
      openErrorNotification(error?.message || "Failed to assign to you");
    }
  };

  const tableDataShowHandler = async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
    try {
      if (!silent) {
        setApiResponse(true);
        setLoading(true);
      }

      // Stage KPI counts only on first page / filter changes (skip on page flips)
      const wantStageCounts = paginationModel.page === 0;
      const stageFilter = expandStageSelection(
        selectedStages,
        allSatgedata
      );
      const basePayload = {
        userId: store.getState().auth.userId,
        pageNo: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search: search || "",
        stage: stageFilter.length ? stageFilter.join(",") : "",
        source: selectedSources.length ? selectedSources.join(",") : "",
        formId: formLead.length > 0 ? formLead.join(",") : "",
        startDate:
          freshFilter || !selectedDateRange?.from
            ? ""
            : selectedDateRange.from,
        endDate:
          freshFilter || !selectedDateRange?.from
            ? ""
            : selectedDateRange.to || selectedDateRange.from,
        assignment: assignmentFilter === "all" ? "" : assignmentFilter,
        uploadLeadId: selectedUploadLeadIds.length
          ? selectedUploadLeadIds.join(",")
          : "",
        memberId: selectedMemberIds.length
          ? selectedMemberIds.join(",")
          : "",
        team_id:
          !selectedMemberIds.length && selectedTeamIds.length
            ? selectedTeamIds.join(",")
            : "",
        fresh: freshFilter ? "1" : "",
        includeStageCounts: 0,
        organizationId:
          activeOrganizationId &&
          activeOrganizationId !== "all" &&
          activeOrganizationId !== "*"
            ? String(activeOrganizationId)
            : "",
      };
      // List without stage $group so rows paint fast; KPIs load after
      const response = await getAllUsers(basePayload);
      if (!response) {
        throw new Error(notifiers.GENERIC_ERROR);
      }
      const rows = Array.isArray(response?.data) ? response.data : [];
      setResponse(rows);
      const listMeta = response?.metadata?.[0] ? response.metadata[0] : {};
      setMetaData((prev: any) => ({
        ...listMeta,
        total: listMeta?.total ?? 0,
        stageCounts: prev?.stageCounts || {},
      }));

      if (wantStageCounts) {
        getAllUsers({
          ...basePayload,
          pageNo: 0,
          pageSize: 1,
          metaOnly: 1,
          includeStageCounts: 1,
        })
          .then((metaResponse) => {
            const kpiMeta = metaResponse?.metadata?.[0];
            if (!kpiMeta) return;
            setMetaData((prev: any) => ({
              ...prev,
              total: kpiMeta.total ?? prev?.total ?? 0,
              stageCounts:
                kpiMeta.stageCounts &&
                Object.keys(kpiMeta.stageCounts || {}).length > 0
                  ? kpiMeta.stageCounts
                  : prev?.stageCounts || {},
            }));
          })
          .catch(() => {});
      }
      if (!rows.length) {
        setUserDataSource([]);
        setUserDataSource1([]);
        return;
      }
      const tabelData = rows.map((userData: any) => {
        return {
          lead: (
            <>
              <span className="block font-yh-bold text-xs text-yh-black">
                {userData?.leadName}
              </span>
              <span className="block font-yh-bold text-xs text-yh-black">{userData?.uid}</span>
            </>
          ),
          property: (
            <>
              <span className="text-[11px] text-yh-black">
                {userData.projecttypeId}
              </span>
            </>
          ),
          contact: (
            <span className="text-[11px] text-yh-black">
              {userData.leadEmail},{userData.leadPhone} <br />{" "}
              {userData.leadPhone}{" "}
            </span>
          ),
          formName: (
            <span className="text-[11px] text-yh-black">
              {userData?.form_name}
            </span>
          ),
          source: (
            <>
              <span className="text-[11px] text-yh-black">
                {userData?.source}
              </span>
            </>
          ),
          stage: <>{userData.AssignToUserStage}</>,
          at: (
            <>
              {(userData?.AssignTo || [])?.length &&
                (userData?.AssignTo || [])?.map((item: any) => {
                  <span key={item.team_id}>{item.team_name}</span>;
                })}
            </>
          ),
          action: (
            <>
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-yh-md text-yh-black hover:bg-yh-lilac"
                onClick={(event: any) =>
                  handlePopOver(
                    event,
                    userData.formId,
                    userData.status,
                    userData
                  )
                }
              >
                <VerticalDotIcon />
              </button>
            </>
          ),
        };
      });
      setUserDataSource([...tabelData]);
      // Arrival order (createdAt / _id). Stage changes must not reorder.
      const sortedLeadData = [...rows].sort(sortLeadsByAssigned);

      setUserDataSource1([...sortedLeadData]);
    } catch (error: any) {
      if (!silent) {
        setUserDataSource([]);
        setUserDataSource1([]);
        setMetaData({ total: 0, page: 0, stageCounts: {} });
        openErrorNotification(
          isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
        );
      }
    } finally {
      if (!silent) {
        setApiResponse(false);
        setLoading(false);
      }
      loadReminders();
    }
  };

  useLeadRealtimeRefresh({
    userId: store.getState().auth.userId,
    showToast: true,
    onSilentRefresh: () => {
      void tableDataShowHandler({ silent: true });
    },
  });

  const handleChangeRadio = (event: any) => {
    setReminderStatus(event.target.value || event.target.defaultValue);
  };
  const handleCloseAddTeam = () => {
    setNewTeamDialog(false);
    tableDataShowHandler();
  };


  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedUser({ ...selectedUser, status: +event.target.value });
  };

  const dialogHeaderContent = () => {
    return (
      <div className="mr-3 mt-1 cursor-pointer">
        <CloseIcon onClick={() => setIsDialogOpen(false)} />
      </div>
    );
  };

  const dialogTitleContent = () => {
    return (
      <div className="px-[70px]">
        <div>
          <h2 className="font-yh-bold text-lg text-yh-black">Account Status</h2>
          <p className="mt-1 text-xs text-yh-light-gray">
            Change your account status
          </p>
        </div>

        <div className="mt-5">
          <p className="font-yh-bold text-sm text-yh-black">Current Status</p>

          <div className="mt-2.5 flex items-center gap-4">
            <CustomRadioButton
              value={1}
              checked={selectedUser.status == 1}
              onChange={handleRadioChange}
              label="Active"
            />

            <CustomRadioButton
              value={0}
              checked={selectedUser.status == 0}
              onChange={handleRadioChange}
              label="Deactive"
            />
          </div>
          <div>
            <CustomButton
              onClick={() => { }}
              label="Delete Account"
              buttonType="iconBtn"
              customClasses={{ color: "red" }}
            />
          </div>
        </div>
        <div className="flex w-[350px] justify-start gap-2.5">
          <CustomButton
            label="Save"
            onClick={() => { }}
            buttonType="primaryBtn"
          />
          <CustomButton
            label="Cancel"
            onClick={() => {
              setIsDialogOpen(false);
            }}
            buttonType="secondaryBtn"
          />
        </div>
      </div>
    );
  };

  const handelDropdownAfterSelect = async (
    ids: any,
    id: any = null,
    assignedMemberIds?: Set<string>
  ) => {
    try {
      const payload = {
        team_id: ids,
        lead_id: id?.id || id?.row?._id || dynamicGridValue?._id,
      };
      const memberIdSet =
        assignedMemberIds && assignedMemberIds.size
          ? assignedMemberIds
          : getAssignedMemberIds(
              filterMembersForAssignedTeams(
                id?.row?.AssignToUser || dynamicGridValue?.AssignToUser,
                getAssignedTeamIds(
                  id?.row?.AssignTo || dynamicGridValue?.AssignTo
                )
              )
            );

      const response1 = await getAllTeamMembersData(payload);
      const data =
        (response1?.data || []).flatMap((member: any) => {
          return (member?.team_members || [])
            .map((mem: any) => {
              if (!mem.users || !mem.users[0]) return null;
              const uid = cleanId(mem.users[0]._id || mem.userId);
              const officeUid = cleanId(mem.userId);
              // Exact match on user _id or office userId — ignore is_available
              const isAssigned =
                memberIdSet.has(uid) ||
                (officeUid ? memberIdSet.has(officeUid) : false);
              return {
                pId: member._id,
                id: cleanId(mem.users[0]._id) || officeUid,
                label: mem.users[0]?.name,
                selected: isAssigned ? 1 : 0,
              };
            })
            .filter(Boolean);
        }) || [];

      // Dedupe by member id (same person on multiple teams)
      type MemberOption = {
        pId: string;
        id: string;
        label: string;
        selected: number;
        isAdmin?: boolean;
      };
      const uniqueById: MemberOption[] = Array.from(
        new Map(
          (data as MemberOption[]).map((m) => [String(m.id), m])
        ).values()
      );

      // Always surface Admin / Super Admin (incl. self) even without team membership
      const auth = store.getState().auth || {};
      const isAdminUser = !!(
        auth.isAdmin ||
        auth.isSuperAdmin ||
        String(auth.hierarchyLevel) === "1"
      );
      if (isAdminUser && auth.userId) {
        const meId = cleanId(auth.userId);
        if (meId && !uniqueById.some((m) => String(m.id) === meId)) {
          uniqueById.unshift({
            pId: "",
            id: meId,
            label: auth.userName
              ? `${auth.userName} (Admin)`
              : "Admin (me)",
            selected: memberIdSet.has(meId) ? 1 : 0,
            isAdmin: true,
          });
        } else if (meId) {
          const idx = uniqueById.findIndex((m) => String(m.id) === meId);
          if (idx >= 0) {
            uniqueById[idx].isAdmin = true;
            if (!/admin/i.test(String(uniqueById[idx].label || ""))) {
              uniqueById[idx].label = `${uniqueById[idx].label} (Admin)`;
            }
          }
        }
      }

      setLeadOptionMembersas(uniqueById);
      const selected = uniqueById.filter((item) => item.selected === 1);
      setSelectedOptionsMembers(selected);
    } catch { }
  };
  const handleOptionCheckboxChange =
    (option: any, value: any) => (event: any, index: any) => {
      if (event.target.checked) {
        const ids = [option.id, ...selectedOptions.map((e: any) => e.id)];
        handelDropdownAfterSelect(ids);
        setIdForTeam((prev: any) => [...prev, option.id]);
        const opt = { ...option, ["selected"]: 1 };
        const objIndex = leadOption.findIndex(
          (item: any) => item.id === option.id
        );
        leadOption[objIndex] = opt;
        setLeadOption((prev: any) => [...leadOption]);
        if (option.teamMembers) {
          setLeadOptionMembersas((prev: any) => {
            return [
              ...prev,

              ...option.teamMembers.map((member: any) => {
                return {
                  pId: option.id,
                  id: member.id,
                  label: member.label,
                  selected: 0,
                };
              }),
            ];
          });
        }
        setSelectedOptions([...selectedOptions, opt]);
      } else {
        setSelectedOptions(
          selectedOptions.filter((item: any) => item.id !== option.id)
        );
        setLeadOptionMembersas(
          leadOptionMembers.filter((item: any) => item?.pId !== option.id)
        );
        const opt = { ...option, ["selected"]: 0 };
        const objIndex = leadOption.findIndex(
          (item: any) => item.id === option.id
        );
        leadOption[objIndex] = opt;
        setLeadOption((prev: any) => [...leadOption]);
        setIdForTeam(idForTeam.filter((item: any) => item !== option?.id));
      }
    };
  const handleOptionCheckboxChange1 =
    (option: any, value: any) => (event: any, index: any) => {
      if (event.target.checked) {
        setIdForMember((prev: any) => [...prev, option.id]);
        const opt = { ...option, ["selected"]: 1 };
        const objIndex = leadOptionMembers.findIndex(
          (item: any) => item.id === option.id
        );
        leadOptionMembers[objIndex] = opt;
        setLeadOptionMembersas((prev: any) => [...leadOptionMembers]);
        setSelectedOptionsMembers([...selectedOptionsMembers, opt]);
      } else {
        setSelectedOptionsMembers(
          selectedOptionsMembers.filter((item: any) => item.id !== option.id)
        );
        const opt = { ...option, ["selected"]: 0 };
        const objIndex = leadOptionMembers.findIndex(
          (item: any) => item.id === option.id
        );
        leadOptionMembers[objIndex] = opt;
        setLeadOptionMembersas((prev: any) => [...leadOptionMembers]);
        setIdForMember(idForMember.filter((item: any) => item !== option?.id));
      }
    };
  const handleAssignToMember = (option: any, isChecked: boolean) => {
    const updatedOption = { ...option, selected: isChecked ? 1 : 0 };

    // Update the leadOptionMembers state immutably
    const updatedLeadOptions = leadOptionMembers.map((item: any) =>
      item.id === option.id ? updatedOption : item
    );

    setLeadOptionMembersas(updatedLeadOptions);

    // Update selected options
    const updatedSelectedOptions = isChecked
      ? [...selectedOptionsMembers, updatedOption]
      : selectedOptionsMembers.filter((item: any) => item.id !== option.id);

    setSelectedOptionsMembers(updatedSelectedOptions);

    // Update the idForMember array
    const updatedIdForMember = isChecked
      ? [...idForMember, option.id]
      : idForMember.filter((id: any) => id !== option.id);

    setIdForMember(updatedIdForMember);
  };

  const handleTeamMultiChange = (values: Array<string | number>) => {
    const nextIds = values.map(String);
    const nextIdSet = new Set(nextIds);
    const byId = new Map(
      (leadOption || []).map((option: any) => [String(option.id), option])
    );
    const nextSelected = nextIds.map((id) => {
      const existing = byId.get(id);
      return existing
        ? { ...existing, selected: 1 }
        : {
            id,
            label: id,
            selected: 1,
            isParent: false,
            teamMembers: null,
          };
    });

    setSelectedOptions(nextSelected);
    setLeadOption((prev: any) => {
      const prevList = Array.isArray(prev) ? prev : [];
      const seen = new Set(prevList.map((option: any) => String(option.id)));
      const extra = nextSelected.filter(
        (option) => !seen.has(String(option.id))
      );
      return [
        ...prevList.map((item: any) => ({
          ...item,
          selected: nextIdSet.has(String(item.id)) ? 1 : 0,
        })),
        ...extra,
      ];
    });
    setIdForTeam(Array.from(nextIds));
    handelDropdownAfterSelect(Array.from(nextIds), {
      id: dynamicGridValue?._id,
      row: dynamicGridValue,
    });

    setLeadOptionMembersas((prevMembers: any) => {
      const nextMembers = nextSelected.flatMap((opt: any) =>
        (opt.teamMembers || []).map((m: any) => {
          const existing = (prevMembers || []).find(
            (x: any) => String(x.id) === String(m.id)
          );
          return {
            pId: opt.id,
            id: m.id,
            label: m.label,
            selected: existing?.selected === 1 ? 1 : 0,
          };
        })
      );
      const byId = new Map<string, any>();
      nextMembers.forEach((m: any) => {
        const key = String(m.id);
        const prev = byId.get(key);
        if (!prev || m.selected === 1) byId.set(key, m);
      });
      return Array.from(byId.values());
    });

    setSelectedOptionsMembers((prev: any) => {
      const allowed = new Set(
        nextSelected.flatMap((opt: any) =>
          (opt.teamMembers || []).map((m: any) => String(m.id))
        )
      );
      // teamMembers often empty until members API returns — don't wipe Sahil/etc.
      if (allowed.size === 0) return prev || [];
      return (prev || []).filter((m: any) => allowed.has(String(m.id)));
    });
  };

  const handleMemberMultiChange = (values: Array<string | number>) => {
    const nextIds = new Set(values.map(String));
    const uniqueMembers = Array.from(
      new Map(
        (leadOptionMembers || []).map((member: any) => [member.id, member])
      ).values()
    );
    const nextSelected = uniqueMembers
      .filter((o: any) => nextIds.has(String(o.id)))
      .map((o: any) => ({ ...o, selected: 1 }));

    setSelectedOptionsMembers(nextSelected);
    setLeadOptionMembersas((prev: any) =>
      (prev || []).map((item: any) => ({
        ...item,
        selected: nextIds.has(String(item.id)) ? 1 : 0,
      }))
    );
    setIdForMember(nextSelected.map((item: any) => item.id));
  };

  const handleDateTimeChange = (event: any) => {
    const selectedDateTime = event?.target?.value || "";
    if (!selectedDateTime || !selectedDateTime.includes("T")) {
      setReminderData((prevFormDataFields: any) => ({
        ...prevFormDataFields,
        date: "",
        time: "",
      }));
      return;
    }
    setReminderData((prevFormDataFields: any) => ({
      ...prevFormDataFields,
      ["date"]: selectedDateTime.split("T")[0],
      ["time"]: selectedDateTime.split("T")[1],
    }));
  };

  const handleSearch = (newValue: string) => {
    setSearchInput(newValue);
  };

  const handleChange = (value: any) => {
    setPaginationModel({
      pageSize: value.target.value,
      page: 0,
    });
  };
  const getRemarkInfo = async (id: any) => {
    try {
      setApiResponse(true);
      const response = await getRemark(id);
      if (response.success == 1) {
        setResponseValueremark((prevFormData: any) => ({
          ...prevFormData,

          value: response?.data,
        }));
      } else {
        setResponseValueremark([]);
      }
    } catch (error: any) {
      // openErrorNotification(
      //   isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      // );
    } finally {
      setApiResponse(false);
    }
  };
  const handelSideSaveRemark = async (body: any) => {
    const payloade = {
      leadId: body.leadId,
      notes: body.notes_remark,
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString(),
      title: "",
    };
    try {
      setApiResponse(true);
      const response = await saveRemark(payloade);
      if (response.success === 1) {
        // setDynamicGridValue("");
        setReminderData((prevData: any) => ({
          ...prevData,
          notes_remark: "",
        }));
      }
      getRemarkInfo(body.leadId);
      tableDataShowHandler();
    } catch (error: any) {
      setApiResponse(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const handelRemoveReminder = async (item: any) => {
    try {
      setApiResponse(true);
      const response = await deleteReminder(item);
      if ((response.sucess = 1)) {
      }
      tableDataShowHandler();
    } catch (error: any) {
      setApiResponse(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const importLeadExcelFile = async () => {
    if (!canExportLeads) {
      openErrorNotification(
        "You do not have permission to export leads. Ask an admin to grant you export access."
      );
      return;
    }
    let objectUrl = "";
    try {
      setdownloadLoading(true);
      const stageFilter = expandStageSelection(selectedStages, allSatgedata);
      const payload = {
        startDate:
          freshFilter || !selectedDateRange?.from
            ? ""
            : selectedDateRange.from,
        endDate:
          freshFilter || !selectedDateRange?.from
            ? ""
            : selectedDateRange.to || selectedDateRange.from,
        formId: formLead.length > 0 ? formLead.join(",") : "",
        organizationId: activeOrganizationId,
        search: search || "",
        stage: stageFilter.length ? stageFilter.join(",") : "",
        source: selectedSources.length ? selectedSources.join(",") : "",
        assignment: assignmentFilter === "all" ? "" : assignmentFilter,
        uploadLeadId: selectedUploadLeadIds.length
          ? selectedUploadLeadIds.join(",")
          : "",
        memberId: selectedMemberIds.length
          ? selectedMemberIds.join(",")
          : "",
        team_id:
          !selectedMemberIds.length && selectedTeamIds.length
            ? selectedTeamIds.join(",")
            : "",
        fresh: freshFilter ? "1" : "",
      };
      const resExcle = await getDownloadLeadsExcle(payload);
      if (resExcle?.success && resExcle.fileData && resExcle.filename) {
        const byteCharacters = atob(resExcle.fileData);
        const byteNumbers = new Array(byteCharacters.length)
          .fill(0)
          .map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = resExcle.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        openSuccessNotification(
          resExcle.count
            ? `Exported ${resExcle.count} leads`
            : "Leads exported"
        );
      } else {
        // makeCall resolves (never throws) on HTTP errors and timeouts, so this
        // branch is the only place the user ever learns the export failed.
        openErrorNotification(
          resExcle?.message ||
            "Could not export leads. Please narrow the date range and try again."
        );
      }
    } catch (err: any) {
      openErrorNotification(
        isTruthy(err?.message) ? err.message : notifiers.GENERIC_ERROR
      );
    } finally {
      if (objectUrl) {
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
      setdownloadLoading(false);
    }
  };


  const outlineBtnClass =
    "inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3.5 text-sm font-medium text-[#374151] transition hover:border-[#D4B8C0] hover:bg-[#F8FAFC]";

  const clearAllFilters = () => {
    setSelectedSources([]);
    setSelectedStages([]);
    setSelectedUploadLeadIds([]);
    setSelectedMemberIds([]);
    setSelectedDepartmentIds([]);
    setSelectedTeamIds([]);
    setTeamFilterOptions([]);
    setTeamMemberFilterOptions([]);
    setFreshFilter(false);
    setAssignmentFilter("all");
    setTableData([]);
    filterLeadByForm([]);
    setSelectedDateRange({ from: null, to: null });
    setSearchInput("");
    setSearch("");
    setIsSearchPending(false);
    clearLeadListFilters(
      FILTER_KEYS.allLeads,
      String(store.getState().auth?.userId || "")
    );
  };

  const applyDrawerFilters = async (next: LeadFilterValues) => {
    const selected = formName.filter((item: any) =>
      next.formIds.some((v) => String(v) === String(item.id))
    );
    setTableData(selected);
    filterLeadByForm(selected.map((item: any) => item.id));
    setSelectedSources(next.sources || []);
    setSelectedStages(next.stages || []);
    setAssignmentFilter(next.assignment || "all");
    setSelectedUploadLeadIds(next.uploadLeadIds || []);
    const deptIds = next.departmentIds || [];
    let teamIds = next.teamIds || [];
    // Department-only → filter by all teams in those departments
    if (!teamIds.length && deptIds.length) {
      try {
        const res = await getTeamsForFilter(deptIds);
        const ids: string[] = [];
        const seen = new Set<string>();
        for (const t of res?.data || []) {
          const tid = String(t._id || "").trim();
          if (tid && !seen.has(tid)) {
            seen.add(tid);
            ids.push(tid);
          }
          for (const st of t.sub_teams || []) {
            const sid = String(st._id || "").trim();
            if (sid && !seen.has(sid)) {
              seen.add(sid);
              ids.push(sid);
            }
          }
        }
        teamIds = ids;
      } catch {
        teamIds = [];
      }
    }
    setSelectedDepartmentIds(deptIds);
    setSelectedTeamIds(teamIds);
    // Member only valid with an explicit team selection from the drawer
    const memberIds =
      (next.teamIds || []).length > 0 ? next.memberIds || [] : [];
    setSelectedMemberIds(memberIds);
    setSelectedDateRange(next.dateRange || { from: null, to: null });
    void loadTeamsForDepartments(deptIds);
    void loadMembersForTeams(next.teamIds || []);
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 }
    );
  };

  const activeFilterCount = useMemo(
    () =>
      countActiveLeadFilters({
        formIds: tableData.map((t: any) => String(t.id)),
        sources: selectedSources,
        stages: selectedStages,
        assignment: assignmentFilter,
        uploadLeadIds: selectedUploadLeadIds,
        memberIds: selectedMemberIds,
        departmentIds: selectedDepartmentIds,
        teamIds: selectedTeamIds,
        dateRange: selectedDateRange || { from: null, to: null },
      }) + (freshFilter ? 1 : 0),
    [
      tableData,
      selectedSources,
      selectedStages,
      assignmentFilter,
      selectedUploadLeadIds,
      selectedMemberIds,
      selectedDepartmentIds,
      selectedTeamIds,
      selectedDateRange,
      freshFilter,
    ]
  );

  const handleFormFilterChange = (values: Array<string | number>) => {
    const selected = formName.filter((item: any) =>
      values.some((v) => String(v) === String(item.id))
    );
    setTableData(selected);
    filterLeadByForm(selected.map((item: any) => item.id));
  };

  const totalLeads = Number(metaData?.total || 0);
  /** Prefer org-wide stageCounts from list API; fall back to current page rows. */
  const countByStageHint = (hints: string[]) => {
    const stageCounts = metaData?.stageCounts;
    if (stageCounts && typeof stageCounts === "object") {
      return Object.entries(stageCounts).reduce((sum, [stageName, count]) => {
        const s = String(stageName || "").toLowerCase();
        if (hints.some((h) => s.includes(h))) {
          return sum + (Number(count) || 0);
        }
        return sum;
      }, 0);
    }
    const pageRows = userDataSource1 || [];
    return pageRows.filter((row: any) => {
      // Prefer lead.stage — AssignToUserStage is often [{ stage: "Initial" }, ...]
      // and Array.join() becomes "[object Object]" which never matches.
      const primary = String(row?.stage || "").toLowerCase();
      const fromAssign = Array.isArray(row?.AssignToUserStage)
        ? row.AssignToUserStage.map((x: any) =>
            typeof x === "string" ? x : x?.stage || ""
          )
            .join(" ")
            .toLowerCase()
        : String(row?.AssignToUserStage || "").toLowerCase();
      const stages = primary || fromAssign;
      return hints.some((h) => stages.includes(h));
    }).length;
  };

  const getShowAllPage = () => {
    return (
      <>
        <div className="flex h-full min-h-0 w-full flex-col">
          {/* Page header — title left, actions right */}
          <div className="mb-3 flex shrink-0 flex-wrap items-start justify-between gap-3 screen:mb-4 screen:gap-4">
            <div className="min-w-0">
              <h3 className="text-xl font-bold tracking-tight text-[#111827] screen:text-xl">
                All Leads
              </h3>
              <p className="mt-1 text-xs text-[#6B7280] screen:text-sm">
                View &amp; manage leads for the organization selected in the header.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={outlineBtnClass}
                onClick={() => history.push(urls.leadsBulkUploadPath)}
              >
                Bulk upload
              </button>
              <button
                type="button"
                className={outlineBtnClass}
                onClick={() => setBulkStatusUploadOpen(true)}
                title="Upload Excel to update lead stages in the background"
              >
                Bulk status upload
              </button>
              {canExportLeads ? (
                <button
                  type="button"
                  className={outlineBtnClass}
                  disabled={downloadLoading}
                  onClick={() => importLeadExcelFile()}
                  title="Export the currently filtered leads to Excel"
                >
                  <DownloadSvg />
                  {downloadLoading ? "Downloading…" : "Download"}
                </button>
              ) : null}

              <button
                type="button"
                className={outlineBtnClass}
                onClick={selectAllPagesMatching}
                disabled={selectAllLoading || !matchingTotal}
                title="Select every lead matching current filters (all pages)"
              >
                {selectAllLoading
                  ? "Selecting…"
                  : matchingTotal
                    ? `Select all ${matchingTotal}`
                    : "Select all"}
              </button>

              <button
                type="button"
                className={outlineBtnClass}
                onClick={selectSourceLeadsOnPage}
                title={
                  selectedSources.length
                    ? `Select all ${selectedSources.join(", ")} leads on this page`
                    : "Select all leads on this page"
                }
              >
                {selectedSources.length === 1
                  ? `Select ${selectedSources[0]} (page)`
                  : selectedSources.length > 1
                    ? `Select sources page (${selectedSources.length})`
                    : "Select page"}
              </button>

              <button
                type="button"
                className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl border border-[#D4B8C0] bg-[#F9F0F4] px-4 text-sm font-semibold text-[#4338CA] transition hover:bg-[#F9F0F4] disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!selectedCount}
                onClick={() => setBulkAssignOpen(true)}
              >
                Re-assign ({selectedCount})
              </button>

              <button
                type="button"
                className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl border border-[#D4B8C0] bg-[#F9F0F4] px-4 text-sm font-semibold text-[#4338CA] transition hover:bg-[#F9F0F4] disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!selectedCount}
                onClick={() => setBulkStageOpen(true)}
              >
                Stage ({selectedCount})
              </button>

              <button
                type="button"
                className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl border border-[#D4B8C0] bg-[#F9F0F4] px-4 text-sm font-semibold text-[#4338CA] transition hover:bg-[#F9F0F4] disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!selectedCount}
                onClick={() => setFeedbackSendOpen(true)}
              >
                Send feedback ({selectedCount})
              </button>

              {privileges &&
                (privileges?.["All Lead"]?.includes("create") ||
                  privileges?.["All Lead"]?.includes("full control")) ? (
                <button
                  type="button"
                  onClick={() => onClickHandler()}
                  className="inline-flex h-10 shrink-0 items-center gap-1 rounded-xl bg-[#A02448] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#841C39]"
                >
                  + Add Lead
                </button>
              ) : null}
            </div>
          </div>

          {/* KPI summary cards — responsive across 1240→2560 */}
          <div
            className={`mb-3 grid shrink-0 gap-2.5 screen:mb-4 screen:gap-3 ${
              dynamicGridValue
                ? "grid-cols-2 md:grid-cols-3 ultrawide:grid-cols-5"
                : "grid-cols-2 md:grid-cols-3 screen:grid-cols-5"
            }`}
          >
            <LeadStatCard
              title="Total Leads"
              value={totalLeads}
              accent="purple"
              icon={<UsersIcon size={20} />}
              loading={loading}
            />
            <LeadStatCard
              title="New Leads"
              value={countByStageHint(["new", "initial", "contact"])}
              accent="blue"
              icon={<UserAddIcon size={20} />}
              loading={loading}
            />
            <LeadStatCard
              title="In Progress"
              value={countByStageHint([
                "prospect",
                "opportunity",
                "follow",
                "progress",
                "lead",
              ])}
              accent="orange"
              icon={<ClockIcon size={20} />}
              loading={loading}
            />
            <LeadStatCard
              title="Converted"
              value={countByStageHint(["closure", "won", "convert", "booked"])}
              accent="green"
              icon={<CheckIconRi size={20} />}
              loading={loading}
            />
            <LeadStatCard
              title="Lost Leads"
              value={countByStageHint(["lost", "dead", "reject"])}
              accent="red"
              icon={<XCircleIcon size={20} />}
              loading={loading}
            />
          </div>

          {/* Search + Filters drawer trigger */}
          <div
            id="all-leads-filters"
            className="mb-3 flex shrink-0 flex-col gap-2.5 desktop:flex-row desktop:flex-wrap desktop:items-center desktop:justify-between desktop:gap-3"
          >
            <div className="w-full min-w-0 desktop:max-w-[320px] desktop:flex-1 screen:max-w-[380px] wide:max-w-[420px]">
              <YhInput
                placeholder="Search by Id, Name, Email and Phone"
                value={searchInput}
                type="search"
                id="outlined-search"
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus={isSearchFocused}
                startAdornment={
                  isSearchPending || (loading && searchInput === search) ? (
                    <SearchSpinner />
                  ) : (
                    <SearchSvg />
                  )
                }
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                containerClassName="w-full"
              />
            </div>

            <div className="flex min-w-0 w-full flex-wrap items-center gap-2 desktop:w-auto desktop:justify-end">
              <button
                type="button"
                onClick={() => {
                  setFreshFilter((v) => !v);
                  setPaginationModel((prev) =>
                    prev.page === 0 ? prev : { ...prev, page: 0 }
                  );
                }}
                className={`inline-flex h-10 items-center gap-2 rounded-xl border px-3.5 text-sm font-semibold transition ${
                  freshFilter
                    ? "border-emerald-600 bg-emerald-50 text-emerald-700"
                    : "border-[#E5E7EB] bg-white text-[#374151] hover:border-[#D4B8C0] hover:bg-[#F8FAFC]"
                }`}
                title="Leads that arrived today (created today)"
              >
                Fresh
              </button>
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="inline-flex h-10 items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3.5 text-sm font-semibold text-[#374151] transition hover:border-[#D4B8C0] hover:bg-[#F8FAFC]"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
                </svg>
                Filters
                {activeFilterCount > 0 ? (
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#A02448] px-1.5 text-[11px] font-bold text-white">
                    {activeFilterCount}
                  </span>
                ) : null}
              </button>
              {activeFilterCount > 0 ? (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="shrink-0 whitespace-nowrap px-2 text-sm font-semibold text-[#A02448] hover:underline"
                >
                  Clear All
                </button>
              ) : null}
            </div>
          </div>

          <LeadFiltersDrawer
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            title="All Leads filters"
            value={{
              formIds: tableData.map((t: any) => String(t.id)),
              sources: selectedSources,
              stages: selectedStages,
              assignment: assignmentFilter,
              uploadLeadIds: selectedUploadLeadIds,
              memberIds: selectedMemberIds,
              departmentIds: selectedDepartmentIds,
              teamIds: selectedTeamIds,
              dateRange: selectedDateRange || { from: null, to: null },
            }}
            onApply={applyDrawerFilters}
            onClear={clearAllFilters}
            formOptions={formName.map((option: any) => ({
              label: option.label,
              value: String(option.id),
            }))}
            sourceOptions={(stageSourse || []).map((option: string) => ({
              label: option,
              value: option,
            }))}
            stageOptions={(stageOptions || [])
              .map((o: any) => String(o?.label || "").trim())
              .filter(Boolean)
              .filter(
                (label: string, i: number, arr: string[]) =>
                  arr.findIndex(
                    (x) => x.toLowerCase() === label.toLowerCase()
                  ) === i
              )
              .map((label: string) => ({ label, value: label }))}
            bulkSetOptions={bulkSetOptions}
            departmentOptions={departmentFilterOptions}
            teamOptions={teamFilterOptions}
            teamMemberOptions={teamMemberFilterOptions}
            memberOptions={memberFilterOptions}
            onDepartmentIdsChange={(ids) => {
              void loadTeamsForDepartments(ids);
            }}
            onTeamIdsChange={(ids) => {
              void loadMembersForTeams(ids);
            }}
          />

          <div className="relative min-h-0 flex-1">
            {allPageSelected &&
            matchingTotal > pageIds.length &&
            !selectAllMatching ? (
              <div className="mb-3 rounded-xl border border-[#DDD6FE] bg-[#F5F3FF] px-4 py-2.5 text-sm text-[#5B21B6]">
                All {pageIds.length} leads on this page are selected.{" "}
                <button
                  type="button"
                  className="font-semibold underline disabled:opacity-50"
                  disabled={selectAllLoading}
                  onClick={selectAllPagesMatching}
                >
                  {selectAllLoading
                    ? "Selecting…"
                    : `Select all ${matchingTotal} matching filters (all pages)`}
                </button>
              </div>
            ) : null}
            {selectAllMatching ? (
              <div className="mb-3 rounded-xl border border-[#A7F3D0] bg-[#ECFDF5] px-4 py-2.5 text-sm text-[#065F46]">
                All {selectedCount} leads matching current filters are selected
                (across pages).{" "}
                <button
                  type="button"
                  className="font-semibold underline"
                  onClick={clearLeadSelection}
                >
                  Clear
                </button>
              </div>
            ) : null}
            {privileges &&
              (privileges?.["All Lead"]?.includes("view") ||
                privileges?.["All Lead"]?.includes("full control")) ? (
              <>
                <YhTable
                  columns={columns}
                  rows={userDataSource1}
                  getRowId={(row) => row._id}
                  rowHeight={80}
                  loading={loading}
                  emptyMessage="No data found"
                  className="h-full min-h-[280px]"
                  onRowClick={(row, e) => {
                    const targetElement = e.target as HTMLElement;
                    if (
                      targetElement?.classList?.contains("action-button") ||
                      targetElement?.closest?.(".action-button") ||
                      targetElement?.closest?.("[data-action-button]")
                    ) {
                      return;
                    }
                    handleRowClick({ row, id: row._id });
                  }}
                  page={paginationModel.page}
                  pageSize={paginationModel.pageSize}
                  totalRows={metaData?.total || 0}
                  onPageChange={(p) => setPaginationModel({ ...paginationModel, page: p })}
                  onPageSizeChange={(s) => setPaginationModel({ pageSize: s, page: 0 })}
                />

                {renderMenu()}
              </>
            ) : (
              ""
            )}
            {anchorEl && (
              <>
                <div className="fixed inset-0 z-40" onClick={handleClose} />
                <div
                  ref={editPopoverRef}
                  className="fixed z-50 flex w-[140px] max-w-full flex-col items-start rounded-yh-md border border-yh-border bg-white p-1 shadow-yh"
                  style={{
                    top: anchorEl.getBoundingClientRect().bottom + 10,
                    left: Math.max(0, anchorEl.getBoundingClientRect().left - 20),
                  }}
                >
                  {privileges &&
                    (privileges?.["All Lead"]?.includes("edit") ||
                      privileges?.["All Lead"]?.includes("full control")) ? (
                    <CustomButton
                      label="Edit"
                      buttonType="iconBtn"
                      onClick={() => {
                        handleEdit(userRow_Id ?? "");
                      }}
                    />
                  ) : (
                    ""
                  )}
                </div>
              </>
            )}
          </div>
          {apiResponse ? <CustomLoader apiResponse={apiResponse} /> : null}
        </div>
        <CustomDialog
          fullScreen={true}
          isDialogOpen={isDialogOpen}
          dialogHeaderContent={dialogHeaderContent()}
          dialogTitleContent={dialogTitleContent()}
        />

        {/* {isDialogOpenLoan && (
          <AddLoan
            isDialogOpenLoan={true}
            handleCloseLoan={handleCloseLoan}
            userId={userId}
            status={status}
          />
        )}

        {isDialogOpenLoanList && (
          <LoanList
            isDialogOpenLoan={true}
            handleCloseLoan={handleCloseLoanList}
            userId={userId}
            status={status}
          />
        )} */}
      </>
    );
  };

  return (
    <>
      <FeedbackBulkSendModal
        open={feedbackSendOpen}
        onClose={() => setFeedbackSendOpen(false)}
        onSent={() => clearLeadSelection()}
        leadIds={selectedLeadIds}
        organizationId={String(activeOrganizationId || "")}
      />
      <BulkAssignModal
        open={bulkAssignOpen}
        onClose={() => setBulkAssignOpen(false)}
        onAssigned={() => {
          clearLeadSelection();
          tableDataShowHandler();
        }}
        leadIds={selectedLeadIds}
        sourceHint={selectedSources.join(", ")}
        defaultOrganizationId={String(activeOrganizationId || "")}
        leadOrganizationIds={(userDataSource1 || [])
          .filter((r: any) => selectedLeadIds.includes(String(r?._id || "")))
          .map((r: any) => String(r?.organizationId || ""))
          .filter(Boolean)}
      />
      <BulkStageModal
        open={bulkStageOpen}
        onClose={() => setBulkStageOpen(false)}
        onUpdated={() => {
          clearLeadSelection();
          tableDataShowHandler();
        }}
        leadIds={selectedLeadIds}
        stageTree={allSatgedata || []}
        stageLabels={stageData || []}
      />
      <BulkStatusUploadModal
        open={bulkStatusUploadOpen}
        onClose={() => setBulkStatusUploadOpen(false)}
        onUpdated={() => {
          tableDataShowHandler();
        }}
        organizationId={
          activeOrganizationId &&
          activeOrganizationId !== "all" &&
          activeOrganizationId !== "*"
            ? String(activeOrganizationId)
            : ""
        }
      />
      <div className="flex h-full min-h-0 w-full gap-0 overflow-hidden">
        {/* Main table area — shrinks when drawer opens */}
        <div
          className={`h-full min-h-0 min-w-0 transition-[flex-basis,width,margin] duration-300 ease-in-out ${
            dynamicGridValue
              ? "w-full flex-[1_1_0%] pr-0 md:pr-3"
              : "w-full flex-[1_1_100%]"
          }`}
        >
          {getShowAllPage()}
        </div>

        {/* Side drawer — narrower on mid screens, wider on ultrawide */}
        <aside
          aria-hidden={!dynamicGridValue}
          className={`relative h-full shrink-0 overflow-hidden transition-[width,opacity] duration-300 ease-in-out ${
            dynamicGridValue
              ? "w-[260px] opacity-100 desktop:w-[280px] screen:w-[300px] wide:w-[320px] ultrawide:w-[360px]"
              : "pointer-events-none w-0 opacity-0"
          }`}
        >
          <div
            className={`relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.06)] transition-transform duration-300 ease-in-out ${
              dynamicGridValue ? "translate-x-0" : "translate-x-full"
            }`}
            style={{ width: "100%", minWidth: dynamicGridValue ? undefined : 0 }}
          >
            <button
              type="button"
              className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
              onClick={() => {
                setReminderStatus("");
                setDynamicGridValue("");
                setShowSideBar(false);
              }}
              aria-label="Close lead details"
            >
              <CloseIcon width={"12px"} height={"12px"} />
            </button>

            <div className="h-full w-full min-w-0 overflow-auto p-3 pt-12 [scrollbar-width:thin] screen:p-4 screen:pt-12">
              {dynamicGridValue ? (
                <LeadDetailsComponent
                  dynamicGridValue={dynamicGridValue}
                  handleAutocompleteChange={handleAutocompleteChange}
                  handleOptionCheckboxChange={handleOptionCheckboxChange}
                  selectedOptions={selectedOptions}
                  leadOption={leadOption}
                  leadOptionMembers={leadOptionMembers}
                  handleOptionCheckboxChange1={handleOptionCheckboxChange1}
                  handleAssignToMember={handleAssignToMember}
                  handleTeamMultiChange={handleTeamMultiChange}
                  handleMemberMultiChange={handleMemberMultiChange}
                  selectedOptionsMembers={selectedOptionsMembers}
                  handleChangeRadio={handleChangeRadio}
                  reminderStatus={reminderStatus}
                  setReminderStatus={setReminderStatus}
                  setReminderData={setReminderData}
                  currentDateTime={currentDateTime}
                  handleDateTimeChange={handleDateTimeChange}
                  handelAssignToUser={handelAssignToUser}
                  handleAssignToMe={handleAssignToMe}
                  onAssigned={() => {
                    tableDataShowHandler();
                  }}
                  handelSideSave={handelSideSave}
                  reminderData={reminderData}
                  responseValuereminder={responseValuereminder}
                  leadReminders={
                    reminders[String(dynamicGridValue?._id || "")] || []
                  }
                  addinationalValue={addinationalValue}
                  stageData={stageData}
                  isMyLead={false}
                  responseValueremark={responseValueremark}
                  handelSideSaveRemark={handelSideSaveRemark}
                  handelRemoveReminder={handelRemoveReminder}
                  onRemindersChange={(rows: any[]) => {
                    setResponseValuereminder({ value: rows });
                    const leadId = String(dynamicGridValue?._id || "");
                    if (leadId) {
                      setReminders((prev: any) => ({ ...prev, [leadId]: rows }));
                    }
                  }}
                  openCustomer={openCustomer}
                  setLeadData={setLeadData}
                  allSatgedata={allSatgedata}
                  sourceOptions={stageSourse}
                />
              ) : null}
            </div>
          </div>
        </aside>

        <YhModal
          open={openModal.open}
          onClose={handleCloseModal}
          width={720}
          title={
            openModal.name === "Stage Details" ? (
              <div className="flex items-start gap-3 pr-2">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F9F0F4] text-[#A02448]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                    <rect x="9" y="3" width="6" height="4" rx="1" />
                    <path d="M9 12h6M9 16h4" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <div className="text-lg font-bold leading-tight text-[#1E1B4B]">
                    Stage Details
                  </div>
                  <p className="mt-0.5 text-sm font-normal text-[#6B7280]">
                    Only people who changed this stage
                  </p>
                </div>
              </div>
            ) : openModal.name === "Assigne-To" ? (
              <div className="flex items-start gap-3 pr-2">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F9F0F4] text-[#A02448]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <div className="text-lg font-bold leading-tight text-[#1E1B4B]">
                    Team List
                  </div>
                  <p className="mt-0.5 text-sm font-normal text-[#6B7280]">
                    View teams and their members
                  </p>
                </div>
              </div>
            ) : openModal.name === "Reminder" ? (
              "Reminder"
            ) : (
              "Team List"
            )
          }
          footer={
            openModal.name === "Stage Details" ||
            openModal.name === "Assigne-To" ? (
              <button
                type="button"
                onClick={handleCloseModal}
                className="inline-flex h-10 min-w-[96px] items-center justify-center rounded-xl border border-[#E5E7EB] bg-white px-5 text-sm font-semibold text-[#374151] transition hover:bg-[#F8FAFC]"
              >
                Close
              </button>
            ) : null
          }
        >
          {openModal.name === "Stage Details" ? (
            <StageDetailsPanel
              stages={openModal?.data?.stage}
              members={openModal?.data?.members}
              leadStage={openModal?.data?.leadStage}
              leadName={openModal?.data?.leadName}
              assignmentMode={openModal?.data?.assignmentMode || "separate"}
              hierarchyMap={hierarchyMap}
            />
          ) : openModal.name === "Reminder" ? (
            <div />
          ) : openModal.name === "Assigne-To" ? (
            (() => {
              const rawTeams = openModal?.data?.team;
              const teams = Array.isArray(rawTeams)
                ? rawTeams.filter(Boolean)
                : rawTeams && typeof rawTeams === "object"
                  ? [rawTeams]
                  : [];
              const rawMembers = openModal?.data?.members;
              const members = Array.isArray(rawMembers)
                ? rawMembers.filter(Boolean)
                : rawMembers && typeof rawMembers === "object"
                  ? [rawMembers]
                  : [];
              const teamIconColors = [
                { bg: "#F9F0F4", color: "#A02448" },
                { bg: "#DBEAFE", color: "#2563EB" },
                { bg: "#D1FAE5", color: "#059669" },
                { bg: "#FFEDD5", color: "#EA580C" },
              ];
              const memberPalette = [
                { bg: "#F9F0F4", color: "#6B1630" },
                { bg: "#DBEAFE", color: "#2563EB" },
                { bg: "#D1FAE5", color: "#059669" },
                { bg: "#FFEDD5", color: "#EA580C" },
                { bg: "#FCE7F3", color: "#DB2777" },
              ];

              // Deduplicate by user id — list only lead assignees (from AssignToUser)
              const uniqueMembers = (() => {
                const seen = new Set<string>();
                const assignedTeamIdSet = new Set(
                  teams.map((t: any) => String(t?._id ?? t?.id ?? ""))
                );
                const filtered = (() => {
                  if (!Array.isArray(members) || !assignedTeamIdSet.size) {
                    return Array.isArray(members) ? members : [];
                  }
                  const matched = members.filter((m: any) => {
                    try {
                      const raw = m?.team_id;
                      if (raw == null || raw === "" || raw === "NA") return true;
                      const arr =
                        typeof raw === "string" ? JSON.parse(raw) : raw;
                      if (!Array.isArray(arr) || arr.length === 0) return true;
                      return arr.some((tid: any) =>
                        assignedTeamIdSet.has(String(tid))
                      );
                    } catch {
                      return true;
                    }
                  });
                  return matched.length ? matched : members;
                })();
                return filtered.filter((m: any) => {
                  const id = String(m?._id ?? m?.id ?? "").trim();
                  if (!id || seen.has(id)) return false;
                  seen.add(id);
                  return true;
                });
              })();

              const membersForTeam = (teamId: string) => {
                const tid = String(teamId ?? "");
                const matched = uniqueMembers.filter((member: any) => {
                  try {
                    const raw = member.team_id;
                    const teamIdArray =
                      typeof raw === "string" ? JSON.parse(raw) : raw;
                    return (
                      Array.isArray(teamIdArray) &&
                      teamIdArray.some((id: any) => String(id) === tid)
                    );
                  } catch {
                    return false;
                  }
                });
                // If team_id isn't set on user docs, still show assigned members once
                if (matched.length > 0) return matched;
                if (teams.length === 1) return uniqueMembers;
                return matched;
              };

              const totalMembers = uniqueMembers.length;

              return (
                <div className="space-y-4">
                  {/* Stats banner */}
                  <div className="flex flex-wrap items-center gap-8 rounded-2xl border border-[#F9F0F4] bg-gradient-to-r from-[#F5F3FF] to-[#F9F0F4] px-5 py-4">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#A02448] shadow-sm">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                        </svg>
                      </span>
                      <div>
                        <p className="text-xs font-medium text-[#6B7280]">
                          Total Teams
                        </p>
                        <p className="text-base font-bold text-[#1E1B4B]">
                          {teams.length}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#A02448] shadow-sm">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </span>
                      <div>
                        <p className="text-xs font-medium text-[#6B7280]">
                          Total Members
                        </p>
                        <p className="text-base font-bold text-[#1E1B4B]">
                          {totalMembers}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Team rows */}
                  <div className="max-h-[340px] overflow-auto rounded-xl border border-[#EEF0F5]">
                    <div className="sticky top-0 z-[1] grid grid-cols-[1fr_1.4fr_24px] gap-3 border-b border-[#EEF0F5] bg-[#F8FAFC] px-4 py-3">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">
                        Team Name
                      </span>
                      <span className="text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">
                        Team Members
                      </span>
                      <span />
                    </div>

                    {teams.length === 0 && uniqueMembers.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-[#9CA3AF]">
                        No teams found
                      </div>
                    ) : teams.length === 0 ? (
                      <div className="grid grid-cols-[1fr_1.4fr_24px] items-center gap-3 border-t border-[#EEF0F5] px-4 py-3.5">
                        <div className="flex min-w-0 items-start gap-2.5">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#F9F0F4] text-[#A02448]">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-[#111827]">
                              Direct assignees
                            </p>
                            <p className="text-xs text-[#9CA3AF]">
                              No team linked on this lead
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {uniqueMembers.map((member: any, mIdx: number) => {
                            const palette = memberPalette[mIdx % memberPalette.length];
                            const name =
                              member?.name ||
                              member?.user_name ||
                              member?.email ||
                              "Member";
                            return (
                              <span
                                key={String(member?._id || member?.id || mIdx)}
                                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                                style={{
                                  backgroundColor: palette.bg,
                                  color: palette.color,
                                }}
                              >
                                {name}
                              </span>
                            );
                          })}
                        </div>
                        <span />
                      </div>
                    ) : (
                      teams.map((item: any, index: number) => {
                        const teamMembers = membersForTeam(item._id);
                        const icon =
                          teamIconColors[index % teamIconColors.length];
                        return (
                          <div
                            key={item._id || index}
                            className="grid grid-cols-[1fr_1.4fr_24px] items-center gap-3 border-t border-[#EEF0F5] px-4 py-3.5"
                          >
                            <div className="flex min-w-0 items-start gap-2.5">
                              <span
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                                style={{
                                  backgroundColor: icon.bg,
                                  color: icon.color,
                                }}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                                  <circle cx="9" cy="7" r="4" />
                                  <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                                </svg>
                              </span>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-bold text-[#111827]">
                                  {item.team_name || "—"}
                                </p>
                                <span className="mt-1 inline-flex rounded-full bg-[#F9F0F4] px-2 py-0.5 text-[11px] font-semibold text-[#A02448]">
                                  {teamMembers.length} Member
                                  {teamMembers.length === 1 ? "" : "s"}
                                </span>
                              </div>
                            </div>

                            <div className="flex min-w-0 flex-col gap-2">
                              {teamMembers.length === 0 ? (
                                <span className="text-xs text-[#9CA3AF]">
                                  No members
                                </span>
                              ) : (
                                teamMembers.map((member: any, idx: number) => {
                                  const name = member.name || "—";
                                  const designation =
                                    (member?.hierarchyId &&
                                      hierarchyMap[member.hierarchyId]) ||
                                    "";
                                  const shortRole = designation
                                    ? designation
                                        .split(" ")
                                        .map((w: string) => w[0])
                                        .join("")
                                        .slice(0, 10)
                                    : "";
                                  const initials =
                                    String(name)
                                      .split(" ")
                                      .filter(Boolean)
                                      .slice(0, 2)
                                      .map((p: string) => p[0]?.toUpperCase())
                                      .join("") || "?";
                                  const avatar =
                                    memberPalette[idx % memberPalette.length];
                                  return (
                                    <div
                                      key={member._id || idx}
                                      className="flex min-w-0 items-center gap-2"
                                    >
                                      <span
                                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
                                        style={{
                                          backgroundColor: avatar.bg,
                                          color: avatar.color,
                                        }}
                                      >
                                        {initials}
                                      </span>
                                      <span className="truncate text-sm font-semibold text-[#111827]">
                                        {name}
                                        {shortRole ? (
                                          <span className="font-normal text-[#9CA3AF]">
                                            {" "}
                                            ({shortRole})
                                          </span>
                                        ) : null}
                                      </span>
                                    </div>
                                  );
                                })
                              )}
                            </div>

                            <span className="flex justify-end text-[#9CA3AF]">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 18l6-6-6-6" />
                              </svg>
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
            <div />
          )}
        </YhModal>

      <YhModal
        open={customerConfirm.open}
        onClose={() =>
          !convertingCustomer &&
          setCustomerConfirm({ open: false, stage: "", lead: null })
        }
        title="Convert to Customer"
        width={420}
        footer={
          <div className="flex justify-end gap-2">
            <YhButton
              variant="secondary"
              size="sm"
              disabled={convertingCustomer}
              onClick={() =>
                setCustomerConfirm({ open: false, stage: "", lead: null })
              }
            >
              No
            </YhButton>
            <YhButton
              variant="primary"
              size="sm"
              disabled={convertingCustomer}
              onClick={confirmConvertCustomer}
            >
              {convertingCustomer ? "Converting..." : "Yes"}
            </YhButton>
          </div>
        }
      >
        <p className="text-sm text-[#374151]">
          Are you sure to make customer?
        </p>
        {customerConfirm.lead?.leadName ? (
          <p className="mt-2 text-xs text-[#6B7280]">
            Lead:{" "}
            <span className="font-semibold text-[#111827]">
              {customerConfirm.lead.leadName}
            </span>
          </p>
        ) : null}
      </YhModal>

      <StageReasonModal
        open={stageReason.open}
        stageLabel={stageReason.stage}
        saving={savingStageReason}
        onCancel={() => setStageReason({ open: false, stage: "", lead: null })}
        onConfirm={confirmStageReason}
      />
      </div>
    </>
  );
};

export default ShowAll;
